/* ============================================================================
   TurnOrder — nav.js  ·  ETAPA 1a — SCHELETUL DE NAVIGARE
   ----------------------------------------------------------------------------
   RO: Bară persistentă (mobil) / sidebar (desktop) cu 5 destinații, trei hub-uri
       tip listă (Club / Mai multe / Profil), FAB de creare și bara de întoarcere.
       Definiția meniului este DECLARATIVĂ (A9.3 pct. 1) și e consumată de o
       SINGURĂ funcție de randare.
   EN: Persistent bottom bar (mobile) / sidebar (desktop) with 5 destinations,
       three list-style hubs, a create FAB and a back bar. The menu definition is
       DECLARATIVE and consumed by a SINGLE render function.

   ── CE NU FACE ACEST FIȘIER (reguli respectate) ──────────────────────────────
   1. NU rescrie nicio funcție de business. `switchTab()` rămâne sursa de adevăr:
      hub-urile și bara îl APELEAZĂ. Singura atingere e un WRAPPER aditiv (același
      pattern folosit deja de `tournaments.js`), care doar randează după el.
   2. NU șterge blocul ascuns `.tabs` din index.html — `canAccessTab()` citește
      `data-cap` din el. Fără el, garda de rol ar cădea TĂCUT.
   3. NU introduce chei noi de localStorage (§26.5). Memorarea poziției vine la
      Etapa 3, în memorie (`state.nav`).
   4. NU introduce `onclick` inline cu date interpolate (§35.1): totul e
      `data-*` + delegare de evenimente.
   5. NU adaugă query-uri, abonamente realtime sau polling (§36). Subtitlurile
      sunt STATICE la această etapă (cele dinamice vin la Etapa 5).
   6. Scripturi CLASICE — fără import/export, scope global partajat (§4.A).

   ── FILTRAREA PE CAPABILITĂȚI ────────────────────────────────────────────────
   Deși planul prevedea filtrarea abia la Etapa 2, constrângerea absolută din
   brief („un rând la care userul nu are drept trebuie ASCUNS, nu blocat la
   click") nu permite un deploy intermediar în care un Membru vede rânduri de
   administrare. Prin urmare filtrarea de bază pe `can(cap)` e ACTIVĂ de acum.
   Ce rămâne pentru Etapa 2: ordonarea grupurilor (A9.1), promovarea grupului
   „Administrare", scurtătura contextuală, tranziția de rol la cald și stările
   speciale de club.

   ── SCARA DE Z-INDEX (C3) — declarată o singură dată, aici și în style.css ────
      bară / sidebar ........ 300     (peste .app-header = 200)
      FAB ................... 350
      meniul FAB ............ 360
      .modal ................ 1000    (existent — acoperă bara, deci o ascunde)
      chat full-screen ...... 9000    (existent)
      .header-popup ......... 9999    (existent)
      overlay GDPR .......... 2147483600 (existent)
   Regula: bara și FAB-ul NU stau deasupra a nimic — sunt sub toate straturile
   blocante, deci dispar vizual de la sine. În plus, `body.chat-fs-lock` le
   ascunde explicit din CSS.
   ========================================================================== */

(function () {
    'use strict';

    if (window.TO_NAV && window.TO_NAV.__stage1a) return;

    /* =====================================================================
       ⚙ COMUTATOR A9.2 — ROLUL ÎN CHIP-UL DE HEADER
       ---------------------------------------------------------------------
       `true`  = sub numele clubului apare rolul meu („Admin principal" etc.)
       `false` = chip-ul arată doar numele clubului
       SCHIMBAREA E DE O SINGURĂ LINIE, AICI. Nimic altceva nu trebuie atins.
       EN: single-line feature flag for showing my role under the club name.
       ===================================================================== */
    var NAV_SHOW_ROLE_IN_HEADER = true;

    /* ======================================================================
       0. HELPERE LOCALE
       ====================================================================== */

    /* Traducere tolerantă: dacă i18n nu e încă gata, cade pe textul de rezervă. */
    function T(key, fallback) {
        try {
            if (typeof window.t === 'function') {
                var v = window.t(key);
                if (v && v !== key) return v;
            }
        } catch (e) { /* i18n indisponibil */ }
        return fallback != null ? fallback : key;
    }

    function esc(s) {
        try { if (typeof window.escHtml === 'function') return window.escHtml(s); } catch (e) { /* noop */ }
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function warn(where, e) {
        try { if (typeof window.logError === 'function') { window.logError('TO_NAV.' + where, e); return; } } catch (e2) { /* noop */ }
        try { console.warn('TO_NAV.' + where + ':', e && e.message); } catch (e3) { /* noop */ }
    }

    /* Capabilitate. FAIL-CLOSED: la orice incertitudine, rândul NU se afișează.
       Ascunderea e inofensivă; afișarea greșită nu e. Garda REALĂ rămâne oricum
       în `switchTab()` / în funcția țintă / în RLS. */
    function CAN(cap) {
        if (!cap) return true;
        try {
            if (typeof window.can !== 'function') return false;
            return !!window.can(cap);
        } catch (e) { warn('CAN', e); return false; }
    }

    /* Owner de platformă (rândul „Panou proprietar"). */
    function IS_OWNER() {
        try { return typeof window.isPlatformOwner === 'function' && !!window.isPlatformOwner(); }
        catch (e) { warn('IS_OWNER', e); return false; }
    }

    function byId(id) { return document.getElementById(id); }

    /* ---------------------------------------------------------------------
       A7 — MEMORIA DE NAVIGARE, NIVELUL 1 (doar în memorie)
       ---------------------------------------------------------------------
       RO: Trăiește în `state.nav`. Am verificat: `saveState()` scrie în
           localStorage DOAR `{ settings, audit, lastSaved }`, deci `state.nav`
           NU ajunge nici în localStorage, nici în `club_settings`. Zero chei noi
           (§26.5), zero scrieri partajate pe club (§23.4). Nu supraviețuiește
           unui reload — și e în regulă, conform specificației.
       EN: lives in `state.nav`; `saveState()` only persists settings/audit/
           lastSaved, so this never reaches storage or the DB. Session-scoped.
       --------------------------------------------------------------------- */
    function NAV() {
        var st = ST();
        if (!st) return { sidebarCollapsed: false };
        /* `sidebarCollapsed` rămâne NEDEFINIT aici intenționat: prima citire îl
           ia din localStorage (vezi `sideCollapsed`).
           `last` (poziția memorată din hub) a fost eliminată — vezi nota de la
           `goBarDestination`. */
        if (!st.nav || typeof st.nav !== 'object') st.nav = {};
        return st.nav;
    }

    /* Cele patru panouri ancorate de header. Listă unică, folosită peste tot. */
    var POPUPS = ['notifPanel', 'clubMenu', 'onlinePanel', 'settingsPanel', 'toSearchPanel', 'toastLogPanel'];

    /* ⚠ CAPCANĂ REALĂ (prinsă în producție, Etapa 1a):
       `app-core.js` declară `let state` / `let currentTab` la nivel de top într-un
       script CLASIC. `let` creează o legătură în scope-ul global LEXICAL, dar NU o
       proprietate pe `window` — deci `window.state` este `undefined`, chiar dacă
       `state` se rezolvă perfect ca identificator liber din orice script încărcat
       ulterior. Citirea trebuie făcută prin identificator, nu prin `window.`.
       `typeof x` pe un identificator nedeclarat NU aruncă, deci e sigur.
       EN: `let` at top level of a classic script creates a global LEXICAL binding,
       not a property of `window`. Read it as a free identifier, never as
       `window.state`. */
    function ST() {
        try { return (typeof state !== 'undefined' && state) ? state : null; }
        catch (e) { return null; }
    }
    function CURTAB() {
        try { return (typeof currentTab !== 'undefined' && currentTab) ? currentTab : ''; }
        catch (e) { return ''; }
    }

    /* ─── Batch 3b (perf): lățimea ecranului, citită O SINGURĂ DATĂ ────────────
       RO: `window.innerWidth` obligă browserul să recalculeze sincron aspectul
           paginii („forced reflow"). `applyVisibility()` scria în DOM și imediat
           CITEA `innerWidth`, de două ori la rând — tiparul clasic de „layout
           thrashing" — iar `refresh()` rulează după FIECARE `render()`.
           Măsurat 08.08.2026 (Lighthouse mobil): 387 ms pierduți doar aici.
           Lățimea se schimbă DOAR la redimensionare, deci o memorăm și o
           reîmprospătăm din ascultătorul de `resize`. Comportament identic.
       EN: `innerWidth` forces a synchronous layout; it was read twice per render
           right after DOM writes. Cached and refreshed on resize instead. */
    var _isDesktopCache = null;

    function _recomputeIsDesktop() { _isDesktopCache = (window.innerWidth > 768); }

    function isDesktop() {
        if (_isDesktopCache === null) _recomputeIsDesktop();
        return _isDesktopCache;
    }

    /* Înregistrat la încărcarea scriptului, NU în `init()`: astfel valoarea rămâne
       corectă chiar dacă fereastra e redimensionată înainte ca `init()` să ruleze.
       Fiind primul ascultător de `resize` înregistrat, se execută înaintea celui
       care apelează `applyVisibility()` — deci acela vede deja valoarea proaspătă. */
    try { window.addEventListener('resize', _recomputeIsDesktop, false); } catch (e) { }

    /* ======================================================================
       1. DEFINIȚIA DECLARATIVĂ (A9.3 pct. 1)
       ----------------------------------------------------------------------
       Structură pură de date. O modificare de meniu = o modificare aici,
       niciodată în funcția de randare.

       Câmpuri de rând:
         id        - identificator stabil (folosit la testarea din consolă)
         icon      - clasă Font Awesome
         titleKey  - cheie i18n (RO+EN) pentru titlu
         subKey    - cheie i18n pentru SUBTITLUL STATIC (obligatoriu)
         subFn     - [Etapa 5] funcție pură de subtitlu dinamic; azi absentă
         cap       - capabilitate necesară (null = vizibil pentru toți)
         owner     - true => vizibil DOAR pentru owner-ul de platformă
         plan      - cheie din FEATURE_MIN_PLAN pentru badge-ul de plan (§25.3)
         go        - { tab:'catalog' }  sau  { fn:'openClubSettings' }
                     `fn` e apelat prin numele lui global, DUPĂ verificarea că
                     există; niciun cod nu e construit din string-uri.
         after     - opțional: id de element spre care derulăm după deschidere
       ====================================================================== */

    /* Cereri de aderare în așteptare pentru clubul activ.
       SURSĂ: `state.pendingByClub` — deja hidratat de `refreshPendingBadge()`.
       ZERO query-uri noi, zero realtime nou, zero polling (§36 / A9.3).
       Nota de securitate: harta e populată DOAR pentru cluburile în care sunt
       admin, deci nu poate scurge numere din cluburi străine. În plus, apelul e
       gardat de `badgeCap`.
       Contract A9.3 pct.2: sincronă, fără efecte secundare, `null` la orice
       incertitudine. */
    /* ======================================================================
       ETAPA 5 — SUBTITLURI DINAMICE
       ----------------------------------------------------------------------
       CONTRACT (A9.3 pct. 2), respectat de fiecare funcție de mai jos:
         · pură și SINCRONĂ — citește doar din `state`, nu declanșează nimic;
         · ZERO query-uri noi, zero abonamente realtime, zero timere (§36):
           fiecare sursă e deja hidratată de aplicație;
         · `null` la orice incertitudine => rândul cade pe subtitlul STATIC;
         · apelul e deja înfășurat în try/catch în `rowHtml`.
       Trei rânduri rămân intenționat STATICE, pentru că sursa lor NU e hidratată
       și ar cere un query nou: Moderare, Turnee, Statisticile mele.

       PLURAL ÎN ROMÂNĂ (A9.3 pct. 4)
       Româna are trei forme: „1 membru", „2 membri", „20 DE membri".
       Regula: n = 1 -> singular; n % 100 == 0 sau >= 20 -> forma cu „de";
       altfel -> plural simplu. Verificat pe cazurile-capcană: 19 membri,
       20 de membri, 101 membri, 120 de membri.
       Pentru engleză, cheile „few" și „many" au același text, deci ACEEAȘI
       regulă produce engleză corectă fără să detectăm limba.
       EN: Romanian needs three plural forms; English keys for `few`/`many` are
       identical, so one selection rule serves both languages.
       ====================================================================== */

    function plural(n, base) {
        var form = (n === 1) ? 'One' : ((n % 100 === 0 || n % 100 >= 20) ? 'Many' : 'Few');
        return T(base + form, '{n}').replace('{n}', String(n));
    }

    function joinParts(parts) {
        var out = [];
        for (var i = 0; i < parts.length; i++) if (parts[i]) out.push(parts[i]);
        return out.length ? out.join(' · ') : null;
    }

    /* Câte zile în urmă, în cuvinte. `null` dacă data lipsește sau e invalidă.
       ⚠ CORECȚIE: prima versiune împărțea milisecundele scurse la 24h. Un mesaj
       de ieri seară la 22:00, citit azi la 10:00, înseamnă 12 ore — deci „azi",
       deși calendaristic a fost IERI. Comparăm acum ZILE CALENDARISTICE locale,
       nu durata scursă.
       EN: compare local calendar days, not elapsed milliseconds — 12h across
       midnight is „yesterday", not „today". */
    function agoText(ts) {
        var t = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (!isFinite(t) || t <= 0) return null;
        var then = new Date(t); then.setHours(0, 0, 0, 0);
        var today = new Date(); today.setHours(0, 0, 0, 0);
        var d = Math.round((today.getTime() - then.getTime()) / 86400000);
        if (d < 0) return null;
        if (d === 0) return T('nav2.d.today', 'azi');
        if (d === 1) return T('nav2.d.yesterday', 'ieri');
        return T('nav2.d.daysAgo', 'acum {n} zile').replace('{n}', String(d));
    }

    /* ---- CLUB ---------------------------------------------------------- */

    function subMembers(st) {
        var n = 0;
        if (st.membersById && typeof st.membersById === 'object') n = Object.keys(st.membersById).length;
        if (!n) return null;
        var txt = plural(n, 'nav2.d.member');
        /* Cererile în așteptare se adaugă DOAR dacă ai dreptul să le vezi
           (A9.3 pct. 7 — subtitlul nu divulgă ce n-ai obține deschizând pagina). */
        if (CAN('member.manage')) {
            var p = pendingJoinCount(st);
            if (p) txt += ' · ' + plural(p, 'nav2.d.request');
        }
        return txt;
    }

    function subChat(st) {
        if (!Array.isArray(st.chatMessages) || !st.chatMessages.length) return null;
        var me = st.currentUser && st.currentUser.id;
        var myEmail = st.currentUser && st.currentUser.email;
        /* Reper de citire: `state.chatReads` (per utilizator, sincronizat prin
           `cs_set_chat_read`) — mai corect decât `lastLogin`, fiindcă ține cont
           de citirile din sesiunea curentă. Dacă lipsește, cădem pe `lastLogin`,
           adică pe formula folosită de badge-ul vechi: nu pierdem funcționalitate. */
        var since = 0;
        if (me && st.chatReads && st.chatReads[me]) since = new Date(st.chatReads[me]).getTime() || 0;
        if (!since) since = new Date((st.currentUser && st.currentUser.lastLogin) || 0).getTime() || 0;

        var unread = 0, last = 0;
        for (var i = 0; i < st.chatMessages.length; i++) {
            var m = st.chatMessages[i];
            var t = new Date(m.timestamp).getTime() || 0;
            if (t > last) last = t;
            if (t > since && m.senderEmail !== myEmail) unread++;
        }
        /* v9 8.1-c3: în memorie stau cel mult ultimele 200 de mesaje ⇒ peste atât,
           numărul exact vine de pe server (chat-core.js, `TO_CHAT.club.unreadTotal`).
           EN: exact count beyond the 200 loaded messages. */
        try {
            var exact = (window.TO_CHAT && window.TO_CHAT.club && window.TO_CHAT.club.unreadTotal) ? window.TO_CHAT.club.unreadTotal(since, unread) : null;
            if (exact != null) unread = exact;
        } catch (e) { /* rămâne numărul din memorie */ }
        if (unread > 0) return plural(unread, 'nav2.d.unread');
        var a = agoText(last);
        return a ? T('nav2.d.lastMsg', 'ultimul mesaj {when}').replace('{when}', a) : null;
    }

    function subPolls(st) {
        if (!Array.isArray(st.polls)) return null;
        var now = Date.now(), n = 0;
        for (var i = 0; i < st.polls.length; i++) {
            var p = st.polls[i];
            if (p && p.active && (new Date(p.expiresAt).getTime() || 0) > now) n++;
        }
        if (!n) return T('nav2.d.noPoll', 'niciun vot deschis');
        return plural(n, 'nav2.d.openPoll');
    }

    function subCatalog(st) {
        if (!st.catalog || typeof st.catalog !== 'object') return null;
        var n = Object.keys(st.catalog).length;
        return n ? plural(n, 'nav2.d.game') : null;
    }

    function subRegulation(st) {
        var r = st.regulation;
        if (!r) return null;
        var parts = [];
        if (Array.isArray(r.articles) && r.articles.length) parts.push(plural(r.articles.length, 'nav2.d.article'));
        var a = r.lastModified ? agoText(r.lastModified) : null;
        if (a) parts.push(T('nav2.d.updated', 'actualizat {when}').replace('{when}', a));
        return joinParts(parts);
    }

    function subHistory(st) {
        if (!Array.isArray(st.games) || !st.games.length) return null;
        var last = 0;
        for (var i = 0; i < st.games.length; i++) {
            var t = new Date(st.games[i].date).getTime() || 0;
            if (t > last) last = t;
        }
        var parts = [plural(st.games.length, 'nav2.d.session')];
        var a = agoText(last);
        if (a) parts.push(T('nav2.d.lastOne', 'ultima {when}').replace('{when}', a));
        return joinParts(parts);
    }

    /* ---- MAI MULTE ------------------------------------------------------ */

    function subAdmin(st) {
        var p = pendingJoinCount(st);
        return p ? plural(p, 'nav2.d.request') : null;
    }

    function subCampaigns(st) {
        if (!Array.isArray(st.campaigns)) return null;
        var n = 0;
        for (var i = 0; i < st.campaigns.length; i++) if (st.campaigns[i] && st.campaigns[i].status === 'active') n++;
        return n ? plural(n, 'nav2.d.activeCampaign') : null;
    }

    /* ---- PROFIL --------------------------------------------------------- */

    function subMyProfile(st) {
        var u = st.currentUser;
        if (!u) return null;
        if (u.userTag) return '@' + u.userTag;
        return u.name || null;
    }

    function subOnline(st) {
        if (!st.onlineUsers || typeof st.onlineUsers !== 'object') return null;
        var n = Object.keys(st.onlineUsers).length;
        return n ? plural(n, 'nav2.d.online') : null;
    }

    function subPlans() {
        try {
            var c = (typeof window.activeClub === 'function') ? window.activeClub() : null;
            if (!c) return null;
            var p = String(c.planType || c.plan || 'starter').toLowerCase();
            var names = { starter: 'Starter', basic: 'Basic', pro: 'Pro', elite: 'Elite', enterprise: 'Enterprise' };
            var label = names[p] || names.starter;
            return T('nav2.d.planNow', 'planul {p}').replace('{p}', label);
        } catch (e) { return null; }
    }

    /* Rapoarte de moderare deschise în clubul activ.
       SURSĂ: `state.openReportsByClub`, populat de `refreshOpenReportsBadge()`
       la deschiderea unui hub (strangulat). Contract A9.3 pct.2: sincronă, fără
       efecte, `null` la orice incertitudine. */
    function openReportsCount(st) {
        if (!st || !st.openReportsByClub) return null;
        var id = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        if (!id) return null;
        var n = st.openReportsByClub[id];
        return (typeof n === 'number' && n > 0) ? n : null;
    }

    function hasPendingJoins() {
        try { return pendingJoinCount(ST() || {}) > 0; } catch (e) { return false; }
    }

    function pendingJoinCount(st) {
        if (!st || !st.pendingByClub) return null;
        var id = (typeof currentClubId !== 'undefined') ? currentClubId : null;
        if (!id) return null;
        var n = st.pendingByClub[id];
        return (typeof n === 'number' && n > 0) ? n : null;
    }

    var MENU = {

        /* ---- Bara / sidebar: 5 destinații, IDENTICE pentru toate rolurile ---- */
        bar: [
            { id: 'home',    icon: 'fas fa-house',        titleKey: 'nav2.home',    tab: 'dashboard' },
            { id: 'club',    icon: 'fas fa-users',        titleKey: 'nav2.club',    tab: 'hubclub' },
            { id: 'agenda',  icon: 'fas fa-calendar-day', titleKey: 'nav2.agenda',  tab: 'calendar' },
            { id: 'more',    icon: 'fas fa-grip',         titleKey: 'nav2.more',    tab: 'hubmore' },
            { id: 'profile', icon: 'fas fa-user',         titleKey: 'nav2.profile', tab: 'hubprofile' }
        ],

        /* ---- Hub-uri ---- */
        hubs: {
            hubclub: {
                titleKey: 'nav2.hub.club',
                barId: 'club',
                groups: [
                    {
                        id: 'people', titleKey: 'nav2.g.people', order: 10, rows: [
                            /* A9.1 (3) — scurtătură contextuală: același rând, marcat vizual
                               + badge când există cereri de aderare în așteptare.
                               `badgeCap` e capabilitatea NUMĂRULUI (poate fi mai strictă
                               decât cea a rândului): un Membru vede „Membri", dar NU
                               află câte cereri sunt. */
                            { id: 'players',    icon: 'fas fa-users',    titleKey: 'nav.players',    subKey: 'nav2.s.players',    subFn: subMembers, cap: null, badgeCap: 'member.manage', badge: pendingJoinCount, go: { tab: 'players' } },
                            /* Rând CONDIȚIONAT: apare doar dacă am dreptul ȘI există cereri.
                               Rezolvă problema semnalată: evidențierea arăta că e ceva de
                               făcut, dar aprobarea se face în alt loc. Acum e la un tap. */
                            { id: 'joinReq',    icon: 'fas fa-envelope-open-text', titleKey: 'nav2.r.joinReq', subKey: 'nav2.s.joinReq', cap: 'member.manage', onlyIf: hasPendingJoins, badgeCap: 'member.manage', badge: pendingJoinCount, go: { fn: 'openJoinRequests' } },
                            { id: 'chat',       icon: 'fas fa-comments', titleKey: 'nav.chat',       subKey: 'nav2.s.chat',       subFn: subChat, cap: null, go: { tab: 'chat' } },
                            { id: 'polls',      icon: 'fas fa-poll',     titleKey: 'nav.polls',      subKey: 'nav2.s.polls',      subFn: subPolls, cap: null, go: { tab: 'polls' } }
                        ]
                    },
                    {
                        id: 'library', titleKey: 'nav2.g.library', order: 20, rows: [
                            { id: 'catalog',    icon: 'fas fa-book',     titleKey: 'nav.catalog',    subKey: 'nav2.s.catalog',    subFn: subCatalog, cap: null, go: { tab: 'catalog' } },
                            { id: 'regulation', icon: 'fas fa-file-alt', titleKey: 'nav.regulation', subKey: 'nav2.s.regulation', subFn: subRegulation, cap: null, plan: 'regulament', go: { tab: 'regulation' } }
                        ]
                    },
                    {
                        id: 'record', titleKey: 'nav2.g.record', order: 30, rows: [
                            { id: 'stats',   icon: 'fas fa-chart-line', titleKey: 'nav.stats',   subKey: 'nav2.s.stats',   cap: null, go: { tab: 'stats' } },
                            { id: 'history', icon: 'fas fa-history',    titleKey: 'nav.history', subKey: 'nav2.s.history', subFn: subHistory, cap: null, go: { tab: 'history' } }
                        ]
                    }
                ]
            },

            hubmore: {
                titleKey: 'nav2.hub.more',
                barId: 'more',
                groups: [
                    /* „Administrare" are order 0 => e mereu primul CÂND există.
                       Pentru Membru toate rândurile cad la filtrare, grupul rămâne
                       gol și dispare de la sine — fără niciun `if (isAdmin)`. */
                    {
                        id: 'admin', titleKey: 'nav2.g.admin', order: 0, separator: true, rows: [
                            { id: 'admin',        icon: 'fas fa-cogs',          titleKey: 'nav.admin',           subKey: 'nav2.s.admin',        subFn: subAdmin, cap: 'member.manage',   badgeCap: 'member.manage', badge: pendingJoinCount, go: { tab: 'admin' } },
                            { id: 'joinReq2',     icon: 'fas fa-envelope-open-text', titleKey: 'nav2.r.joinReq', subKey: 'nav2.s.joinReq', cap: 'member.manage', onlyIf: hasPendingJoins, badgeCap: 'member.manage', badge: pendingJoinCount, go: { fn: 'openJoinRequests' } },
                            { id: 'clubSettings', icon: 'fas fa-sliders',       titleKey: 'nav2.r.clubSettings', subKey: 'nav2.s.clubSettings', cap: 'settings.edit',   go: { fn: 'openClubSettings' } },
                            { id: 'moderation',   icon: 'fas fa-shield-halved', titleKey: 'nav2.r.moderation',   subKey: 'nav2.s.moderation',   cap: 'moderation.view', badgeCap: 'moderation.view', badge: openReportsCount, go: { tab: 'admin' }, after: 'moderationPanel', openAcc: 'adminSec-moderation' },
                            { id: 'ownerPanel',   icon: 'fas fa-user-shield',   titleKey: 'nav2.r.ownerPanel',   subKey: 'nav2.s.ownerPanel',   cap: null, owner: true, go: { fn: 'openOwnerPanel' } }
                        ]
                    },
                    {
                        id: 'community', titleKey: 'nav2.g.community', order: 10, rows: [
                            { id: 'discoverClubs',   icon: 'fas fa-compass',   titleKey: 'nav2.r.discoverClubs',   subKey: 'nav2.s.discoverClubs',   cap: null, go: { fn: 'openDiscoverClubs' } },
                            { id: 'discoverPlayers', icon: 'fas fa-user-plus', titleKey: 'nav2.r.discoverPlayers', subKey: 'nav2.s.discoverPlayers', cap: null, go: { fn: 'openDiscoverPlayers' } }
                        ]
                    },
                    {
                        id: 'competition', titleKey: 'nav2.g.competition', order: 20, rows: [
                            { id: 'tournaments', icon: 'fas fa-trophy', titleKey: 'tourn.navTitle', subKey: 'nav2.s.tournaments', cap: null, plan: 'tournaments', go: { tab: 'tournaments' } },
                            { id: 'campaigns',   icon: 'fas fa-scroll', titleKey: 'nav.campaigns',  subKey: 'nav2.s.campaigns',   subFn: subCampaigns, cap: null, plan: 'campaigns',   go: { tab: 'campaigns' } }
                        ]
                    },
                    {
                        id: 'tools', titleKey: 'nav2.g.tools', order: 30, rows: [
                            { id: 'randomizers', icon: 'fas fa-dice', titleKey: 'nav.randomizers', subKey: 'nav2.s.randomizers', cap: null, go: { tab: 'randomizers' } }
                        ]
                    }
                ]
            },

            hubprofile: {
                titleKey: 'nav2.hub.profile',
                barId: 'profile',
                groups: [
                    {
                        id: 'account', titleKey: 'nav2.g.account', order: 10, rows: [
                            { id: 'myProfile', icon: 'fas fa-id-card', titleKey: 'nav.profile',    subKey: 'nav2.s.myProfile', subFn: subMyProfile, cap: null, go: { tab: 'profile' } },
                            { id: 'myStats',   icon: 'fas fa-globe',   titleKey: 'nav2.r.myStats', subKey: 'nav2.s.myStats',   cap: null, plan: 'crossClubStats', go: { fn: 'openCrossClubStats' } }
                        ]
                    },
                    {
                        id: 'app', titleKey: 'nav2.g.app', order: 30, rows: [
                            { id: 'settings', icon: 'fas fa-cog',          titleKey: 'settings.title',  subKey: 'nav2.s.settings', cap: null, panel: 'settingsPanel', go: { fn: 'toggleSettingsPanel' } },
                            { id: 'plans',    icon: 'fas fa-rocket',          titleKey: 'nav2.r.plans',    subKey: 'nav2.s.plans',    subFn: subPlans, cap: null, go: { fn: 'openPlansModal' } },
                            { id: 'feedback', icon: 'fas fa-comment-dots', titleKey: 'feedback.button', subKey: 'nav2.s.feedback', cap: null, go: { fn: 'openFeedback' } },
                            { id: 'logout',   icon: 'fas fa-sign-out-alt', titleKey: 'nav.logout',      subKey: 'nav2.s.logout',   cap: null, danger: true, go: { fn: 'logout' } }
                        ]
                    }
                ]
            }
        },

        /* ---- Meniul FAB „Ce vrei să creezi?" (A2) ---- */
        create: [
            { id: 'session', icon: 'fas fa-dice',          titleKey: 'nav2.create.session', primary: true, cap: 'session.create',      go: { fn: '_navNewSession' } },
            { id: 'event',   icon: 'fas fa-calendar-plus', titleKey: 'nav2.create.event',   cap: 'event.manage',       go: { fn: '_navNewEvent' } },
            { id: 'poll',    icon: 'fas fa-poll',          titleKey: 'nav2.create.poll',    cap: 'session.create',     clubRank: 2, go: { fn: '_navNewPoll' } },
            { id: 'ann',     icon: 'fas fa-bullhorn',      titleKey: 'nav2.create.ann',     cap: 'announcements.post', go: { fn: 'postAnnouncement' } }
        ]
    };

    /* Tab -> care buton din bară se evidențiază.
       Derivat DIN definiție, ca să nu existe două surse de adevăr. */
    function barIdForTab(tab) {
        var i, h, g, r, rows;
        for (i = 0; i < MENU.bar.length; i++) if (MENU.bar[i].tab === tab) return MENU.bar[i].id;
        for (h in MENU.hubs) {
            if (!Object.prototype.hasOwnProperty.call(MENU.hubs, h)) continue;
            for (g = 0; g < MENU.hubs[h].groups.length; g++) {
                rows = MENU.hubs[h].groups[g].rows;
                for (r = 0; r < rows.length; r++) {
                    if (rows[r].go && rows[r].go.tab === tab) return MENU.hubs[h].barId;
                }
            }
        }
        return null;
    }

    /* ======================================================================
       2. ACȚIUNI DE CREARE (A2)
       ----------------------------------------------------------------------
       Fiecare apelează EXACT funcția existentă, cu gărzile existente. Nu
       reimplementează nimic. `requireWritableClub()` (§21.5) e aplicat acolo
       unde funcția-țintă nu îl aplică ea însăși.
       ====================================================================== */

    function writable() {
        try {
            if (typeof window.requireWritableClub === 'function') return !!window.requireWritableClub();
        } catch (e) { warn('writable', e); return false; }
        return true;
    }

    /* ⚠ „Sesiune nouă" din FAB trebuie să însemne CHIAR una nouă.
       `switchTab('session')` singur NU golește formularul: app-core.js păstrează
       intenționat lucrul în curs (`sessionFormHasWork()`), ca să nu-ți piardă
       scorurile tastate la comutarea între taburi. Corect pentru un tab, GREȘIT
       pentru un buton care promite „creează". Dacă erai legat de o sesiune live,
       ajungeai fix în ea.
       Golirea o face funcția existentă `clearSessionForm()` — exact cea indicată
       în comentariul din `sessionFormHasWork`. Dacă ai lucru nesalvat, întrebăm
       întâi: un buton de creare nu are voie să arunce tăcut ce ai scris.
       EN: the FAB promises a NEW session, so it must clear the form (existing
       `clearSessionForm()`), asking first if there is unsaved work. */
    window._navNewSession = function () {
        if (!writable()) return;

        function start() {
            try {
                if (typeof window.clearSessionForm === 'function') window.clearSessionForm();
                if (typeof window.switchTab === 'function') window.switchTab('session');
                if (typeof window.setSessionType === 'function') window.setSessionType('standard');
            } catch (e) { warn('_navNewSession.start', e); }
        }

        /* ⚠ NU întrebăm niciodată din cauza unei sesiuni LIVE.
           Un club poate avea 5 mese deschise simultan; „Sesiune nouă" trebuie să
           deschidă a 6-a, imediat, fără dialog. Sesiunile live trăiesc pe server
           și rămân intacte: de la corecția din 4c, `clearSessionForm()` se dezleagă
           ÎNAINTE de a goli câmpurile, deci golirea nu mai trimite nimic către ele.
           Întrebăm DOAR dacă ai o ciornă locală NElive (joc/jucători tastați și
           nesalvați nicăieri) — aceea chiar s-ar pierde.
           EN: never prompt because of a live session (they live on the server and
           stay intact). Prompt only for a local, non-live draft that would be lost. */
        var boundToLive = false;
        try { boundToLive = !!window._liveSessionId; } catch (e) { boundToLive = false; }
        if (boundToLive) { start(); return; }

        var hasWork = false;
        try { hasWork = (typeof window.sessionFormHasWork === 'function') && !!window.sessionFormHasWork(); }
        catch (e) { warn('_navNewSession.hasWork', e); }

        if (!hasWork) { start(); return; }

        try {
            if (typeof window.confirmModal === 'function') {
                Promise.resolve(window.confirmModal(T('nav2.newSession.confirm',
                    'Ai o sesiune în lucru. Vrei să o abandonezi și să începi una nouă?')))
                    .then(function (yes) {
                        if (yes) start();
                        else if (typeof window.switchTab === 'function') window.switchTab('session');
                    }, function () { /* anulat */ });
                return;
            }
        } catch (e) { warn('_navNewSession.confirm', e); }
        start();
    };

    window._navNewEvent = function () {
        /* `addEventModal()` nu are gardă proprie; `saveEvent()` are (event.manage +
           requireWritableClub). Punem garda și la deschidere, ca să nu oferim un
           formular care oricum ar fi respins la salvare. */
        if (!writable()) return;
        if (!CAN('event.manage')) {
            try { window.showNotification(T('rbac.noPermission', 'Nu ai permisiunea pentru această acțiune.'), 'error'); } catch (e) { warn('_navNewEvent', e); }
            return;
        }
        try { if (typeof window.addEventModal === 'function') window.addEventModal(); }
        catch (e) { warn('_navNewEvent', e); }
    };

    window._navNewPoll = function () {
        /* `openCreatePollModal()` are deja garda proprie `roleRank < 2`. */
        if (!writable()) return;
        /* 8.16-3d: polls.js se încarcă la cerere ⇒ încarcă, apoi deschide (direct doar un modul de
           aceeași versiune, TO_CHAT.readyModule). Înainte, fără fișier, clicul se pierdea în tăcere. */
        try {
            var T = window.TO_CHAT;
            if (T && T.readyModule && T.readyModule('TO_POLLS')) window.openCreatePollModal();
            else if (T && T.useModule) T.useModule('polls.js', 'TO_POLLS', function () { window.openCreatePollModal(); });
        }
        catch (e) { warn('_navNewPoll', e); }
    };

    /* ======================================================================
       3. CONSTRUIREA NODURILOR (o singură dată, în <body>)
       ----------------------------------------------------------------------
       Bara și FAB-ul stau în <body>, NU în #mainApp: `_detachMainApp()` (§19.4)
       scoate #mainApp din DOM la logout, iar bara ar dispărea cu el.
       ====================================================================== */

    /* Pliere sidebar (A4). Starea trăiește DOAR în memorie la această etapă;
       persistența (`state.nav`) vine la Etapa 3, conform planului. */
    /* Starea sidebar-ului folosește ACELAȘI mecanism ca poziția din hub-uri
       (cerința A4: „se memorează conform regulii de la A7"). */
    /* Starea sidebar-ului e o preferință de DISPOZITIV, nu de club și nu de cont:
       pe laptop îl vrei desfășurat, pe un ecran mic pliat. De aceea localStorage
       (o singură cheie, cu prefixul `to_` folosit deja de proiect) și NU
       `club_settings` — acolo ar fi o scriere partajată pe club (§23.4).
       Poziția din hub-uri rămâne doar în memorie, ca până acum.
       EN: device-level preference -> one localStorage key, never club_settings. */
    var LS_SIDEBAR = 'to_nav_sidebar';

    function sideCollapsed() {
        var n = NAV();
        if (n.sidebarCollapsed === undefined) {
            var v = null;
            try { v = localStorage.getItem(LS_SIDEBAR); } catch (e) { v = null; }
            n.sidebarCollapsed = (v === '1');
        }
        return !!n.sidebarCollapsed;
    }

    function setSideCollapsed(v) {
        NAV().sidebarCollapsed = !!v;
        try { localStorage.setItem(LS_SIDEBAR, v ? '1' : '0'); }
        catch (e) { warn('setSideCollapsed', e); }   /* mod privat / cotă plină */
    }

    function applySideCollapsed() {
        document.body.classList.toggle('to-side-collapsed', sideCollapsed());
    }

    /* Butonul stă ÎN sidebar, deasupra destinațiilor — nu în header.
       Motiv: `.app-header` are `justify-content: space-between`; un al treilea
       copil împingea wordmark-ul „TurnOrder" în centru. Aici e și mai aproape de
       ce controlează. Randat de `renderBar()`, ca să nu existe două locuri care
       construiesc conținutul barei. */
    function sideToggleHtml() {
        return '<button type="button" id="toSideToggle" class="to-side-toggle"' +
            ' data-nav-side="1" aria-expanded="' + (sideCollapsed() ? 'false' : 'true') + '"' +
            ' title="' + esc(T(sideCollapsed() ? 'nav2.side.expand' : 'nav2.side.collapse', 'Meniu')) + '"' +
            ' aria-label="' + esc(T(sideCollapsed() ? 'nav2.side.expand' : 'nav2.side.collapse', 'Meniu')) + '">' +
            '<i class="fas fa-' + (sideCollapsed() ? 'bars' : 'chevron-left') + '"></i>' +
            '</button>';
    }

    function ensureNodes() {
        if (!byId('toNav')) {
            var nav = document.createElement('nav');
            nav.id = 'toNav';
            nav.className = 'to-nav hidden';
            nav.setAttribute('role', 'navigation');
            document.body.appendChild(nav);
        }
        if (!byId('toFab')) {
            var fab = document.createElement('button');
            fab.type = 'button';
            fab.id = 'toFab';
            fab.className = 'to-fab hidden';
            fab.setAttribute('aria-haspopup', 'true');
            fab.setAttribute('aria-expanded', 'false');
            fab.innerHTML = '<i class="fas fa-plus"></i>';
            document.body.appendChild(fab);
        }
        if (!byId('toFabMenu')) {
            var m = document.createElement('div');
            m.id = 'toFabMenu';
            m.className = 'to-fabmenu hidden';
            m.setAttribute('role', 'menu');
            document.body.appendChild(m);
        }
        /* 29 oct. 2026 (cererea lui Alex): fundal blurat cât e deschis meniul de creare. Doar
           decor: un clic pe el închide meniul prin calea existentă („clic în afara meniului"),
           fără ascultător nou. EN: blurred scrim behind the open create menu. */
        if (!byId('toFabScrim')) {
            var sc = document.createElement('div');
            sc.id = 'toFabScrim';
            sc.className = 'to-fab-scrim';
            sc.setAttribute('aria-hidden', 'true');
            document.body.appendChild(sc);
        }
    }

    /* ======================================================================
       4. RANDARE — o SINGURĂ funcție pentru toate hub-urile
       ====================================================================== */

    /* Un rând e vizibil dacă trece de capabilitate ȘI de garda de owner. */
    function rowVisible(row) {
        /* Demo: `isPlatformOwner()` e adevărat intenționat (dă toate drepturile), dar
           panoul proprietarului nu are ce căuta acolo. EN: hide owner rows in demo. */
        if (row.owner && (!IS_OWNER() || (typeof window.isDemoMode === 'function' && window.isDemoMode()))) return false;
        if (!CAN(row.cap)) return false;
        /* `onlyIf` = condiție de STARE (nu de securitate). Se evaluează DUPĂ
           capabilitate, ca să nu poată deveni niciodată o portiță. */
        if (typeof row.onlyIf === 'function') {
            try { if (!row.onlyIf()) return false; } catch (e) { warn('onlyIf:' + row.id, e); return false; }
        }
        return true;
    }

    function planBadge(row) {
        if (!row.plan) return '';
        try { if (typeof window.planBadgeHtml === 'function') return window.planBadgeHtml(row.plan, true); }
        catch (e) { warn('planBadge', e); }
        return '';
    }

    function rowHtml(hubId, row) {
        /* Subtitlu: la Etapa 1a doar cel STATIC. Contractul de la A9.3 (funcție
           pură + try/catch + fallback pe static) e deja pregătit aici. */
        var sub = T(row.subKey, '');
        if (typeof row.subFn === 'function') {
            try {
                var d = row.subFn(ST() || {});
                if (d) sub = d;
            } catch (e) { warn('subFn:' + row.id, e); /* rămâne subtitlul static */ }
        }
        /* Badge de acțiuni în așteptare. Respectă `badgeCap`, care poate fi mai
           strictă decât capabilitatea rândului (A9.3 pct.7: un subtitlu/badge nu
           divulgă informație pe care userul n-ar obține-o deschizând destinația). */
        var n = null;
        if (typeof row.badge === 'function' && CAN(row.badgeCap)) {
            try { n = row.badge(ST() || {}); } catch (e) { warn('badge:' + row.id, e); n = null; }
        }
        var flagged = (typeof n === 'number' && n > 0);
        var badgeHtml = flagged
            ? '<span class="to-row-badge">' + esc(n > 99 ? '99+' : String(n)) + '</span>'
            : '';

        return '' +
            '<button type="button" class="to-row' + (row.danger ? ' is-danger' : '') +
            (flagged ? ' is-flagged' : '') + '"' +
            ' data-nav-row="' + esc(row.id) + '" data-nav-hub="' + esc(hubId) + '">' +
            '<span class="to-row-ic"><i class="' + esc(row.icon) + '"></i></span>' +
            '<span class="to-row-tx">' +
            '<span class="to-row-t">' + esc(T(row.titleKey, row.id)) + planBadge(row) + '</span>' +
            '<span class="to-row-s">' + esc(sub) + '</span>' +
            '</span>' +
            badgeHtml +
            '<i class="fas fa-chevron-right to-row-ch"></i>' +
            '</button>';
    }

    /* Cardul „eu" din capul hub-ului Profil: poză, nume, @tag și planul.
       Citește DOAR din stare deja hidratată (`state.currentUser`, `activeClub()`),
       deci zero query-uri. Tap pe el => „Profilul meu". */
    function meCardHtml() {
        var st = ST();
        var u = st && st.currentUser;
        if (!u) return '';

        var name = u.name || u.displayName || '';
        if (!name) return '';

        /* ⚠ `state.currentUser.avatar` e adesea GOL — poza reală stă în
           `state.users[email]` sau în `membersById[id]`. Tabul Profil folosește
           exact lanțul ăsta (`renderMyProfile`: `u.avatar || m.avatar`); îl
           reproducem, altfel cardul arăta silueta implicită deși ai poză.
           EN: the real avatar lives in `state.users`/`membersById`, not on
           `currentUser` — same fallback chain as `renderMyProfile`. */
        var src = u.avatar || '';
        if (!src) {
            try {
                var mm = (typeof window.memberById === 'function') ? window.memberById(u.id) : null;
                if (mm && mm.avatar) src = mm.avatar;
            } catch (e) { warn('meCard.memberById', e); }
        }
        if (!src) {
            try {
                var uu = (st.users && u.email) ? st.users[u.email] : null;
                if (uu && uu.avatar) src = uu.avatar;
            } catch (e) { warn('meCard.users', e); }
        }

        var av = '';
        try { if (src && typeof window.avatarHtml === 'function') av = window.avatarHtml(src, 56); }
        catch (e) { warn('meCard.avatar', e); }
        if (!av) {
            var ini = name.trim().split(/\s+/).slice(0, 2).map(function (w) { return w.charAt(0); }).join('');
            av = '<span class="to-me-ini">' + esc(ini.toUpperCase()) + '</span>';
        }

        var bits = [];
        if (u.userTag) bits.push('<span class="to-me-tag">@' + esc(u.userTag) + '</span>');
        /* RO: „Membru din <luna anul>" — vechimea CONTULUI (`profiles.created_at`, deja
               hidratat în `state.currentUser.createdAt`, deci ZERO request suplimentar).
               A înlocuit eticheta de plan, care citea `activeClub().planType`: aceea era
               planul CLUBULUI ACTIV, nu al utilizatorului, așa că un simplu membru dintr-un
               club Elite își vedea propriul nume marcat „Elite". Informație greșită
               într-un loc care descrie persoana, nu clubul.
               Fără dată validă (ex. utilizatorul demo) nu afișăm nimic — nu inventăm date.
           EN: account age from `profiles.created_at`; replaced the plan badge, which showed
               the ACTIVE CLUB's plan next to the user's own name. */
        try {
            var cd = u.createdAt ? new Date(u.createdAt) : null;
            if (cd && isFinite(cd.getTime())) {
                var loc = (window.currentLang === 'ro') ? 'ro-RO' : 'en-GB';
                var when = cd.toLocaleDateString(loc, { month: 'long', year: 'numeric' });
                var lbl = T('nav.memberSince', 'Membru din {date}').replace('{date}', when);
                bits.push('<span class="to-me-since">' + esc(lbl) + '</span>');
            }
        } catch (e) { warn('meCard.since', e); }

        return '<button type="button" class="to-me" data-nav-row="myProfile" data-nav-hub="hubprofile">' +
            '<span class="to-me-av">' + av + '</span>' +
            '<span class="to-me-tx">' +
            '<span class="to-me-name">' + esc(name) + '</span>' +
            (bits.length ? '<span class="to-me-sub">' + bits.join('<span class="to-me-dot">·</span>') + '</span>' : '') +
            '</span></button>';
    }

    function renderHub(hubId) {
        var host = byId('tab-' + hubId);
        var def = MENU.hubs[hubId];
        if (!host || !def) return;

        var groups = def.groups.slice().sort(function (a, b) { return (a.order || 0) - (b.order || 0); });
        var html = '<div class="to-hub">';
        if (hubId === 'hubprofile') html += meCardHtml();
        var any = false;
        var i, j, g, rows;

        for (i = 0; i < groups.length; i++) {
            g = groups[i];
            rows = g.rows.filter(rowVisible);
            if (!rows.length) continue;          /* grup gol => dispare complet */
            any = true;
            html += '<div class="to-grp' + (g.separator ? ' to-grp-sep' : '') + '" data-nav-grp="' + esc(g.id) + '">' +
                '<div class="to-grp-t">' + esc(T(g.titleKey, g.id)) + '</div>' +
                '<div class="to-grp-rows">';
            for (j = 0; j < rows.length; j++) html += rowHtml(hubId, rows[j]);
            html += '</div></div>';
        }

        if (!any) html += '<div class="to-hub-empty">' + esc(T('nav2.empty', 'Nimic disponibil aici.')) + '</div>';
        html += '</div>';
        host.innerHTML = html;
        /* ⚠ CORECȚIE: `innerHTML` șterge footerul legal dacă acesta stă în hub-ul
           Profil (cazul mobil). Îl repunem AICI, la sursă, ca orice cale care
           re-randează un hub să-l restaureze — inclusiv re-randarea asincronă
           declanșată de reîmprospătarea cererilor de aderare, care ocolea
           `refresh()` și îl lăsa dispărut până la următorul refresh de pagină.
           EN: restore the legal footer right after any hub re-render. */
        placeLegalFooter();
    }

    function renderBar() {
        var nav = byId('toNav');
        if (!nav) return;
        var active = barIdForTab(CURTAB());
        var html = sideToggleHtml();
        for (var i = 0; i < MENU.bar.length; i++) {
            var b = MENU.bar[i];
            var on = (b.id === active);
            html += '<button type="button" class="to-nav-item' + (on ? ' is-active' : '') + '"' +
                ' data-nav-bar="' + esc(b.id) + '"' + (on ? ' aria-current="page"' : '') + '>' +
                '<i class="' + esc(b.icon) + '"></i>' +
                '<span class="to-nav-lb">' + esc(T(b.titleKey, b.id)) + '</span>' +
                '</button>';
        }
        nav.innerHTML = html;
        /* `innerHTML` a șters footerul dacă era în sidebar; îl repunem. */
        placeLegalFooter();
    }

    /* ---- Bara de întoarcere (A5): „← Club" în capul paginii deschise ---- */
    var _cameFrom = null;

    function clearBackBar() {
        var old = byId('toBackBar');
        if (old && old.parentNode) old.parentNode.removeChild(old);
    }

    function renderBackBar() {
        clearBackBar();
        if (!_cameFrom || !MENU.hubs[_cameFrom]) return;
        var tab = CURTAB();
        if (!tab || MENU.hubs[tab]) return;                 /* suntem chiar într-un hub */
        var host = byId('tab-' + tab);
        if (!host) return;
        var name = T(MENU.hubs[_cameFrom].titleKey, _cameFrom);
        var b = document.createElement('button');
        b.type = 'button';
        b.id = 'toBackBar';
        b.className = 'to-backbar';
        b.setAttribute('data-nav-back', _cameFrom);
        b.innerHTML = '<i class="fas fa-arrow-left"></i><span>' + esc(name) + '</span>';
        host.insertBefore(b, host.firstChild);
    }

    /* ======================================================================
       5. MENIUL FAB
       ====================================================================== */

    var _fabOpen = false;

    /* 8.16-4e (1 nov. 2026): rangul din CLUBUL curent (fail-closed). `can()` îl lasă pe proprietarul
       platformei în orice club (și demo-ul), dar fereastra „Vot nou" (`openCreatePollModal`) și serverul
       (`poll_create`) cer rangul din club ≥ 2 ⇒ rândul apărea și apoi spunea „Nu ai permisiunea".
       EN: club rank for rows whose target checks it (the platform-owner override does not apply there). */
    function CLUB_RANK() {
        try { return (typeof window.roleRank === 'function' && typeof window.currentClubMyRole === 'function') ? (window.roleRank(window.currentClubMyRole()) || 0) : 0; }
        catch (e) { warn('CLUB_RANK', e); return 0; }
    }

    function createRows() {
        var out = [];
        for (var i = 0; i < MENU.create.length; i++) {
            var c = MENU.create[i];
            if (c.cap && !CAN(c.cap)) continue;
            if (c.clubRank && CLUB_RANK() < c.clubRank) continue;
            out.push(c);
        }
        return out;
    }

    function renderFabMenu() {
        var m = byId('toFabMenu');
        if (!m) return;
        var rows = createRows();
        var html = '<div class="to-fabmenu-t">' + esc(T('nav2.create.title', 'Ce vrei să creezi?')) + '</div>';
        if (!rows.length) {
            html += '<div class="to-fabmenu-empty">' + esc(T('nav2.create.none', 'Nu ai drepturi de creare în acest club.')) + '</div>';
        } else {
            for (var i = 0; i < rows.length; i++) {
                var c = rows[i];
                html += '<button type="button" class="to-fabmenu-i' + (c.primary ? ' is-primary' : '') + '"' +
                    ' role="menuitem" data-nav-create="' + esc(c.id) + '">' +
                    '<i class="' + esc(c.icon) + '"></i><span>' + esc(T(c.titleKey, c.id)) + '</span>' +
                    '</button>';
            }
        }
        m.innerHTML = html;
    }

    function openFab() {
        renderFabMenu();
        var m = byId('toFabMenu'), f = byId('toFab');
        if (!m || !f) return;
        m.classList.remove('hidden');
        var sc = byId('toFabScrim');
        if (sc) sc.classList.add('is-on');
        f.classList.add('is-x');
        f.innerHTML = '<i class="fas fa-times"></i>';
        f.setAttribute('aria-expanded', 'true');
        f.title = T('nav2.create.close', 'Închide');
        _fabOpen = true;
    }

    function closeFab() {
        var m = byId('toFabMenu'), f = byId('toFab'), sc = byId('toFabScrim');
        if (m) m.classList.add('hidden');
        if (sc) sc.classList.remove('is-on');
        if (f) {
            f.classList.remove('is-x');
            f.innerHTML = '<i class="fas fa-plus"></i>';
            f.setAttribute('aria-expanded', 'false');
            f.title = T('nav2.create.open', 'Creează');
        }
        _fabOpen = false;
    }

    function toggleFab() { if (_fabOpen) closeFab(); else openFab(); }

    /* ======================================================================
       5b. FOOTERUL LEGAL — își schimbă locul în funcție de context
       ----------------------------------------------------------------------
       RO: Neautentificat => rămâne unde a fost dintotdeauna, la baza paginii.
           Autentificat pe desktop => coboară în sidebar, sub destinații.
           Autentificat pe mobil  => intră la baza hub-ului Profil, ca să nu mai
           mănânce spațiu pe fiecare ecran. Rămâne la un singur tap distanță.
       Mutăm NODUL existent (nu-l duplicăm): linkurile au `onclick` inline, care
       supraviețuiesc mutării. Ținem un marcaj în DOM ca să-l putem pune înapoi
       exact de unde l-am luat.
       EN: the legal footer moves into the sidebar (desktop) or to the bottom of
       the Profile hub (mobile) when signed in; otherwise it stays put. The node
       is MOVED, never cloned, and we keep an anchor comment to restore it.
       ====================================================================== */

    var _footerAnchor = null;
    var _footerNode = null;     /* referință PERSISTENTĂ: `innerHTML` pe gazdă
                                   scoate nodul din document, dar referința
                                   rămâne validă și îl putem re-atașa. */

    function placeLegalFooter() {
        try {
            var f = _footerNode || document.querySelector('footer.legal-footer');
            if (!f) return;
            _footerNode = f;
            /* O SINGURĂ dată: scoatem separatorii „·" care erau noduri de text
               între linkuri. Nu se pot stiliza din CSS, iar cu fiecare link pe
               rândul lui rămâneau puncte orfane pe rânduri separate. De acum
               separatorul e generat din CSS (`::after`), deci îl putem porni și
               opri în funcție de unde stă footerul.
               EN: strip the literal „·" text nodes once; separators become CSS
               generated content so each layout can control them. */
            if (!f.__toSepStripped) {
                f.__toSepStripped = true;
                var kids = Array.prototype.slice.call(f.childNodes);
                for (var i = 0; i < kids.length; i++) {
                    if (kids[i].nodeType === 3) {
                        var txt = (kids[i].nodeValue || '').replace(/[\s\u00a0\u00b7·]/g, '');
                        if (!txt) f.removeChild(kids[i]);
                    }
                }
            }
            if (!_footerAnchor && f.parentNode) {
                _footerAnchor = document.createComment('legal-footer-home');
                f.parentNode.insertBefore(_footerAnchor, f);
            }
            var target;
            if (!appVisible()) {
                target = null;                                   /* poziția originală */
            } else if (isDesktop()) {
                target = byId('toNav');
            } else {
                target = byId('tab-hubprofile');
            }
            f.classList.toggle('in-nav', target && target.id === 'toNav');
            f.classList.toggle('in-hub', !!(target && target.id === 'tab-hubprofile'));
            if (!target) {
                if (_footerAnchor && _footerAnchor.parentNode && f.previousSibling !== _footerAnchor) {
                    _footerAnchor.parentNode.insertBefore(f, _footerAnchor.nextSibling);
                }
                return;
            }
            if (f.parentNode !== target) target.appendChild(f);
            else if (target.lastElementChild !== f) target.appendChild(f);
        } catch (e) { warn('placeLegalFooter', e); }
    }

    /* ======================================================================
       5c. A9.2 — ROLUL MEU SUB NUMELE CLUBULUI (în spatele comutatorului)
       ----------------------------------------------------------------------
       RO: Chip-ul de club (`#clubSwitcher`) e randat de `renderClubSwitcher()`
           din supabase-integration.js, care scrie DOAR în `#clubSwitcherName`
           (verificat) și nu reconstruiește nodul. Deci putem adăuga o a doua
           linie fără să atingem funcția: împachetăm o singură dată eticheta
           existentă într-o coloană și punem rolul dedesubt.
           Eticheta vine din `clubRoleLabel()` — ACELAȘI strat de capabilități
           ca peste tot, cu cheile `role.*` care există deja în RO și EN. Zero
           text hardcodat, zero mapare duplicată de roluri.
           Se actualizează la fiecare `refresh()`, deci urmează tranziția de rol
           la cald și comutarea de club, exact ca bara.
       EN: the chip is rendered by `renderClubSwitcher()`, which only sets the
           name node. We wrap it once and append the role line, sourced from
           `clubRoleLabel()` (same capability layer, existing RO/EN keys).
       ====================================================================== */

    /* Poza clubului în locul iconiței generice cu trei oameni. Sursa e
       `activeClub().imageUrl` (deja hidratat din `clubs.image_url`) — zero
       query-uri. Dacă nu are poză, rămâne iconița, ca până acum. */
    function syncClubChipAvatar() {
        var lead = document.querySelector('#clubSwitcher .hbtn-lead');
        if (!lead) return;
        var url = null;
        try {
            var c = (typeof window.activeClub === 'function') ? window.activeClub() : null;
            if (c && c.imageUrl) url = String(c.imageUrl);
        } catch (e) { warn('syncClubChipAvatar', e); }

        if (!url) {
            if (lead.tagName === 'IMG') {
                var ic = document.createElement('i');
                ic.className = 'fas fa-users hbtn-lead';
                lead.parentNode.replaceChild(ic, lead);
            }
            return;
        }
        if (lead.tagName === 'IMG') {
            if (lead.getAttribute('src') !== url) lead.setAttribute('src', url);
            return;
        }
        var img = document.createElement('img');
        img.className = 'hbtn-lead to-club-pic';
        img.setAttribute('src', url);
        img.setAttribute('alt', '');
        /* Dacă poza nu se încarcă (link mort), revenim la iconiță. */
        img.onerror = function () {
            try {
                var ic2 = document.createElement('i');
                ic2.className = 'fas fa-users hbtn-lead';
                if (img.parentNode) img.parentNode.replaceChild(ic2, img);
            } catch (e) { warn('clubPic.onerror', e); }
        };
        lead.parentNode.replaceChild(img, lead);
    }

    function syncHeaderRole() {
        syncClubChipAvatar();
        var chip = byId('clubSwitcher');
        var nameEl = byId('clubSwitcherName');
        if (!chip || !nameEl) return;

        var roleEl = byId('toClubRole');

        if (!NAV_SHOW_ROLE_IN_HEADER) {
            if (roleEl && roleEl.parentNode) roleEl.parentNode.removeChild(roleEl);
            chip.classList.remove('has-role');
            return;
        }

        /* Împachetăm o singură dată numele într-o coloană. */
        var stack = chip.querySelector('.to-chip-stack');
        if (!stack) {
            stack = document.createElement('span');
            stack.className = 'to-chip-stack';
            nameEl.parentNode.insertBefore(stack, nameEl);
            stack.appendChild(nameEl);
        }
        if (!roleEl) {
            roleEl = document.createElement('span');
            roleEl.id = 'toClubRole';
            roleEl.className = 'to-chip-role';
            stack.appendChild(roleEl);
        }

        var label = '', slug = 'member';
        try {
            var r = (typeof window.currentClubMyRole === 'function') ? window.currentClubMyRole() : null;
            if (r && typeof window.clubRoleLabel === 'function') label = window.clubRoleLabel(r) || '';
            /* Aceeași mapare rol -> slug ca în `updateUserDisplay`, ca să folosim
               EXACT culorile din `.role-pill` (user management din Admin). */
            slug = ({ principal_admin: 'principal', secondary_admin: 'secondary',
                      moderator: 'moderator', member: 'member' })[r] || 'member';
        } catch (e) { warn('syncHeaderRole', e); label = ''; }
        roleEl.className = 'to-chip-role to-role-' + slug;

        var demo = false;
        try { demo = (typeof window.isDemoMode === 'function') && !!window.isDemoMode(); } catch (e) { demo = false; }

        if (!label || demo) {
            roleEl.textContent = '';
            roleEl.classList.add('hidden');
            chip.classList.remove('has-role');
        } else {
            roleEl.textContent = label;
            roleEl.classList.remove('hidden');
            chip.classList.add('has-role');
        }
    }

    /* ======================================================================
       5d. ETAPA 6 — CĂUTAREA ÎN CLUBUL ACTIV (secțiunea D)
       ----------------------------------------------------------------------
       REGULI RESPECTATE
       · Filtrare 100% ÎN MEMORIE. Zero query-uri, zero realtime, zero timere
         periodice (§36). Toate cele cinci surse sunt deja hidratate.
       · Doar clubul ACTIV — `membersById`, `catalog`, `games`, `events` sunt
         deja limitate la el prin hidratare.
       · ANTI-SCURGERE: căutarea nu poate găsi nimic ce n-ai vedea deschizând
         pagina. Rândurile de navigare trec prin `rowVisible()` (aceleași
         capabilități ca meniul). La membri NU căutăm și NU afișăm NICIODATĂ
         e-mailul — doar nume, nume afișat și @tag, adică exact ce e public în
         lista de membri.
       · Rezultatele nu reimplementează filtrarea: te duc în tabul potrivit ȘI
         completează caseta de căutare EXISTENTĂ a acelui tab (`#playerSearch`,
         `#gameSearch`, `#historySearch`), lăsând filtrul aplicației să lucreze.
       · Diacritice: „stefan" găsește „Ștefan" și invers.
       EN: fully in-memory search over already-hydrated state; capability-filtered;
       never touches e-mail; reuses each tab's existing filter box.
       ====================================================================== */

    var Q_MIN = 2;          /* sub 2 litere nu căutăm */
    var Q_PER_GROUP = 5;    /* câte rezultate arătăm per grup */
    var _qTimer = null, _qLast = '', _qIndex = -1;

    /* Normalizare pentru comparație: minuscule + fără diacritice.
       NFD desface „Ș" în „S" + semn, deci merge și pentru varianta cu virgulă
       și pentru cea cu sedilă, fără liste de excepții. */
    function norm(v) {
        try {
            return String(v == null ? '' : v)
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
        } catch (e) { return String(v == null ? '' : v).toLowerCase(); }
    }

    function hit(hay, needle) { return norm(hay).indexOf(needle) !== -1; }

    /* ---------------------------------------------------------------------
       CĂUTARE BILINGVĂ pentru numele de secțiuni.
       ---------------------------------------------------------------------
       RO: Cineva care ține aplicația în engleză scrie totuși „acasă" din reflex.
           Numele secțiunilor sunt SINGURELE lucruri traduse din căutare (membrii,
           jocurile și sesiunile sunt date, deci limba nu le schimbă). Așa că
           potrivim interogarea cu eticheta din TOATE limbile din dicționar, dar
           AFIȘĂM și navigăm în limba curentă a aplicației.
           `window.I18N` e deja expus de i18n.js — nu e nevoie de nicio modificare
           acolo. Bucla merge peste orice limbă adăugată în viitor, nu doar RO/EN.
       EN: match section names against every language in the dictionary, but show
           and navigate in the app's current language. Data (members, games,
           sessions) is language-independent, so it is matched as-is.
       --------------------------------------------------------------------- */
    function labelsAll(key, fallback) {
        var out = [];
        var cur = T(key, fallback);
        if (cur) out.push(cur);
        try {
            var D = window.I18N;
            if (D && typeof D === 'object') {
                for (var lang in D) {
                    if (!Object.prototype.hasOwnProperty.call(D, lang)) continue;
                    var v = D[lang] && D[lang][key];
                    if (v && typeof v === 'string' && out.indexOf(v) === -1) out.push(v);
                }
            }
        } catch (e) { warn('labelsAll', e); }
        return out;
    }

    function hitAny(list, needle) {
        for (var i = 0; i < list.length; i++) if (hit(list[i], needle)) return true;
        return false;
    }

    function searchAll(raw) {
        var q = norm(raw).trim();
        var out = { nav: [], members: [], games: [], sessions: [], events: [], total: 0 };
        if (q.length < Q_MIN) return out;
        var st = ST() || {};
        var i, k, keys;

        /* --- 1. Secțiuni ale aplicației (cerință explicită) --------------- */
        try {
            for (var h in MENU.hubs) {
                if (!Object.prototype.hasOwnProperty.call(MENU.hubs, h)) continue;
                var groups = MENU.hubs[h].groups;
                for (var g = 0; g < groups.length; g++) {
                    var rows = groups[g].rows;
                    for (var r = 0; r < rows.length; r++) {
                        var row = rows[r];
                        if (!rowVisible(row)) continue;          /* aceleași drepturi ca meniul */
                        var title = T(row.titleKey, row.id);      /* afișăm în limba curentă */
                        /* ...dar potrivim în toate limbile */
                        if (hitAny(labelsAll(row.titleKey, row.id), q) ||
                            hitAny(labelsAll(row.subKey, ''), q)) {
                            out.nav.push({ kind: 'nav', hub: h, id: row.id, icon: row.icon, label: title,
                                           meta: T(MENU.hubs[h].titleKey, h) });
                        }
                    }
                }
            }
            for (i = 0; i < MENU.bar.length; i++) {
                var b = MENU.bar[i];
                if (MENU.hubs[b.tab]) continue;                  /* hub-urile sunt deja mai sus */
                var bt = T(b.titleKey, b.id);
                if (hitAny(labelsAll(b.titleKey, b.id), q)) {
                    out.nav.push({ kind: 'tab', tab: b.tab, icon: b.icon, label: bt, meta: '' });
                }
            }
        } catch (e) { warn('search.nav', e); }

        /* --- 2. Membri — NUME/@tag, niciodată e-mail --------------------- */
        try {
            if (st.membersById && typeof st.membersById === 'object') {
                keys = Object.keys(st.membersById);
                for (i = 0; i < keys.length; i++) {
                    var m = st.membersById[keys[i]];
                    if (!m || m.hidden) continue;
                    var nm = m.displayName || m.name || '';
                    var tag = m.userTag || '';
                    if (hit(nm, q) || (tag && hit(tag, q))) {
                        out.members.push({ kind: 'member', id: keys[i], label: nm,
                                           meta: tag ? '@' + tag : '', icon: 'fas fa-user' });
                    }
                }
            }
        } catch (e) { warn('search.members', e); }

        /* --- 3. Jocuri din catalog --------------------------------------- */
        try {
            if (st.catalog && typeof st.catalog === 'object') {
                keys = Object.keys(st.catalog);
                for (i = 0; i < keys.length; i++) {
                    var c = st.catalog[keys[i]] || {};
                    if (hit(keys[i], q) || (c.category && hit(c.category, q))) {
                        out.games.push({ kind: 'game', label: keys[i], meta: c.category || '',
                                         icon: 'fas fa-dice' });
                    }
                }
            }
        } catch (e) { warn('search.games', e); }

        /* --- 4. Sesiuni din istoric -------------------------------------- */
        try {
            if (Array.isArray(st.games)) {
                for (i = 0; i < st.games.length; i++) {
                    var sgame = st.games[i] || {};
                    var name = sgame.gameName || '';
                    var who = Array.isArray(sgame.participants) ? sgame.participants.join(' ') : '';
                    var whoNames = '';
                    if (who) {
                        try {
                            whoNames = sgame.participants.map(function (pid) {
                                var mm = st.membersById && st.membersById[pid];
                                return (mm && (mm.displayName || mm.name)) || String(pid);
                            }).join(' ');
                        } catch (e2) { whoNames = who; }
                    }
                    if (hit(name, q) || (whoNames && hit(whoNames, q))) {
                        var when = sgame.timestamp ? new Date(sgame.timestamp) : null;
                        out.sessions.push({ kind: 'session', label: name || '—',
                                            meta: (when && isFinite(when.getTime())) ? when.toLocaleDateString() : '',
                                            icon: 'fas fa-history' });
                    }
                }
            }
        } catch (e) { warn('search.sessions', e); }

        /* --- 5. Evenimente ----------------------------------------------- */
        try {
            if (Array.isArray(st.events)) {
                for (i = 0; i < st.events.length; i++) {
                    var ev = st.events[i] || {};
                    if (hit(ev.title || '', q) || hit(ev.location || '', q) || hit(ev.description || '', q)) {
                        var ed = ev.date ? new Date(ev.date) : null;
                        out.events.push({ kind: 'event', label: ev.title || '—',
                                          meta: (ed && isFinite(ed.getTime())) ? ed.toLocaleDateString() : (ev.location || ''),
                                          icon: 'fas fa-calendar-day' });
                    }
                }
            }
        } catch (e) { warn('search.events', e); }

        out.total = out.nav.length + out.members.length + out.games.length + out.sessions.length + out.events.length;
        return out;
    }

    /* ---------------------------------------------------------------------
       FILTRUL APLICAT DIN CĂUTARE SE CURĂȚĂ CÂND PLECI DIN TAB
       ---------------------------------------------------------------------
       RO: Căutarea globală completează caseta tabului (ex. `#gameSearch`), ca să
           aterizezi fix pe ce căutai. Dacă rămâne acolo după ce pleci și te
           întorci mai târziu, catalogul pare gol fără motiv — n-ai tastat tu
           nimic, deci n-ai de unde să știi că e un filtru activ.
           ⚠ Curățăm DOAR ce am scris NOI, și doar dacă valoarea e încă neatinsă.
           Dacă între timp ai modificat caseta, ai preluat-o tu — o lăsăm în pace.
           E același principiu ca la scorurile din sesiunea live: nu suprascriem
           niciodată ce a schimbat utilizatorul.
       EN: clear the tab filter WE injected when leaving that tab — but only if
           the user has not since edited it. Never touch user-owned input.
       --------------------------------------------------------------------- */
    var _appliedFilter = null;   /* { tab, inputId, value } */

    function fillTabSearch(tab, inputId, value) {
        _appliedFilter = { tab: tab, inputId: inputId, value: value };
        setTimeout(function () {
            try {
                var el = byId(inputId);
                if (!el) { _appliedFilter = null; return; }
                el.value = value;
                el.dispatchEvent(new Event('input', { bubbles: true }));
            } catch (e) { warn('fillTabSearch', e); _appliedFilter = null; }
        }, 60);
    }

    function clearStaleFilter() {
        if (!_appliedFilter) return;
        if (CURTAB() === _appliedFilter.tab) return;      /* încă suntem acolo */
        var f = _appliedFilter;
        _appliedFilter = null;
        try {
            var el = byId(f.inputId);
            if (!el) return;
            if (el.value !== f.value) return;             /* utilizatorul a preluat caseta */
            el.value = '';
            el.dispatchEvent(new Event('input', { bubbles: true }));
        } catch (e) { warn('clearStaleFilter', e); }
    }

    function runResult(res) {
        closeSearch();
        try {
            if (res.kind === 'nav') { activateRow(res.hub, res.id, null); return; }
            if (res.kind === 'tab') { _cameFrom = null; goTab(res.tab); return; }
            if (res.kind === 'member') { _cameFrom = 'hubclub'; goTab('players', { after: function () { fillTabSearch('players', 'playerSearch', res.label); } }); return; }
            if (res.kind === 'game') { _cameFrom = 'hubclub'; goTab('catalog', { after: function () { fillTabSearch('catalog', 'gameSearch', res.label); } }); return; }
            if (res.kind === 'session') { _cameFrom = 'hubclub'; goTab('history', { after: function () { fillTabSearch('history', 'historySearch', res.label); } }); return; }
            if (res.kind === 'event') { _cameFrom = null; goTab('calendar'); return; }
        } catch (e) { warn('runResult', e); }
    }

    var _qResults = [];   /* lista aplatizată, pentru navigarea cu tastele */

    function renderSearch(raw) {
        var panel = byId('toSearchPanel');
        if (!panel) return;
        var body = panel.querySelector('.to-q-body');
        if (!body) return;

        _qResults = [];
        var q = String(raw || '').trim();
        if (norm(q).length < Q_MIN) {
            body.innerHTML = '<div class="to-q-hint">' + esc(T('nav2.q.hint', 'Scrie cel puțin 2 litere.')) + '</div>';
            return;
        }

        var res = searchAll(q);
        if (!res.total) {
            body.innerHTML = '<div class="to-q-hint">' +
                esc(T('nav2.q.empty', 'Niciun rezultat pentru „{q}".').replace('{q}', q)) + '</div>';
            return;
        }

        var groups = [
            { key: 'nav', titleKey: 'nav2.q.nav', items: res.nav },
            { key: 'members', titleKey: 'nav2.q.members', items: res.members },
            { key: 'games', titleKey: 'nav2.q.games', items: res.games },
            { key: 'sessions', titleKey: 'nav2.q.sessions', items: res.sessions },
            { key: 'events', titleKey: 'nav2.q.events', items: res.events }
        ];

        var html = '';
        for (var gi = 0; gi < groups.length; gi++) {
            var gr = groups[gi];
            if (!gr.items.length) continue;
            html += '<div class="to-q-grp"><div class="to-q-grp-t">' + esc(T(gr.titleKey, gr.key)) + '</div>';
            var shown = Math.min(gr.items.length, Q_PER_GROUP);
            for (var ii = 0; ii < shown; ii++) {
                var it = gr.items[ii];
                var idx = _qResults.length;
                _qResults.push(it);
                html += '<button type="button" class="to-q-item" data-nav-qi="' + idx + '">' +
                    '<i class="' + esc(it.icon) + '"></i>' +
                    '<span class="to-q-lb">' + esc(it.label) + '</span>' +
                    (it.meta ? '<span class="to-q-meta">' + esc(it.meta) + '</span>' : '') +
                    '</button>';
            }
            if (gr.items.length > shown) {
                html += '<div class="to-q-more">' +
                    esc(T('nav2.q.more', '+{n} încă').replace('{n}', String(gr.items.length - shown))) + '</div>';
            }
            html += '</div>';
        }
        body.innerHTML = html;
        _qIndex = -1;
    }

    function ensureSearchPanel() {
        if (byId('toSearchPanel')) return;
        var d = document.createElement('div');
        d.id = 'toSearchPanel';
        d.className = 'header-popup to-q hidden';
        d.innerHTML =
            '<div class="to-q-head">' +
            '<i class="fas fa-search"></i>' +
            '<input type="text" id="toSearchInput" class="to-q-input" autocomplete="off" spellcheck="false">' +
            '<button type="button" class="to-q-x" data-nav-qclose="1" aria-label="Close"><i class="fas fa-times"></i></button>' +
            '</div><div class="to-q-body"></div>';
        document.body.appendChild(d);
        var inp = byId('toSearchInput');
        if (inp) {
            inp.addEventListener('input', function () {
                var v = inp.value;
                if (v === _qLast) return;
                _qLast = v;
                clearTimeout(_qTimer);
                _qTimer = setTimeout(function () { renderSearch(v); }, 160);
            }, false);
        }
    }

    function openSearch() {
        ensureSearchPanel();
        var p = byId('toSearchPanel'), inp = byId('toSearchInput');
        if (!p || !inp) return;
        inp.placeholder = T('nav2.q.placeholder', 'Caută în club…');
        p.classList.remove('hidden');
        placeHeaderPopup(p);
        renderSearch(inp.value);
        try { inp.focus(); inp.select(); } catch (e) { /* fără focus pe unele browsere */ }
    }

    function closeSearch() {
        var p = byId('toSearchPanel');
        if (p) p.classList.add('hidden');
        _qIndex = -1;
    }

    function moveQ(step) {
        var items = document.querySelectorAll('#toSearchPanel .to-q-item');
        if (!items.length) return;
        _qIndex += step;
        if (_qIndex < 0) _qIndex = items.length - 1;
        if (_qIndex >= items.length) _qIndex = 0;
        for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-cur', i === _qIndex);
        try { items[_qIndex].scrollIntoView({ block: 'nearest' }); } catch (e) { /* noop */ }
    }

    /* ======================================================================
       6. VIZIBILITATE
       ====================================================================== */

    function appVisible() {
        var app = byId('mainApp');
        var st = ST();
        return !!(app && !app.classList.contains('hidden') && st && st.currentUser);
    }

    function applyVisibility() {
        var show = appVisible();
        var nav = byId('toNav'), fab = byId('toFab');
        if (nav) nav.classList.toggle('hidden', !show);

        /* ⚠ Lupa apărea și pe ecranul de autentificare. `hideHeaderActions()`
           (app-core.js) ascunde o listă FIXĂ de id-uri, iar `#toSearchBtn` a fost
           adăugat după ea, deci nu era în listă. E acum ascuns implicit în HTML
           și îl arătăm doar odată cu aplicația — n-are ce căuta la login, unde
           nu există nici club în care să cauți.
           EN: `hideHeaderActions()` hides a fixed id list that predates this
           button; show it only when the app itself is visible. */
        var qb = byId('toSearchBtn');
        if (qb) qb.classList.toggle('hidden', !show);
        if (!show) closeSearch();
        /* FAB-ul apare DOAR dacă userul poate crea măcar un lucru. Un Membru
           simplu nu poate crea nimic (toate acțiunile cer rang >= 2), deci un
           buton care ar deschide un panou gol nu i se arată deloc. */
        /* Stări speciale de club (read-only / blocat / arhivat / peste limită):
           `requireWritableClub()` respinge orice creare, deci FAB-ul ar fi un
           buton mort. Îl ascundem. Rândurile de meniu RĂMÂN vizibile — ai nevoie
           de Admin și Setări club tocmai ca să vezi/rezolvi starea. */
        var readOnly = false;
        try { readOnly = (typeof window.isClubReadOnly === 'function') && !!window.isClubReadOnly(); }
        catch (e) { warn('isClubReadOnly', e); }
        var canCreate = false;
        try { canCreate = !readOnly && createRows().length > 0; } catch (e) { warn('applyVisibility', e); }
        if (fab) fab.classList.toggle('hidden', !(show && canCreate));
        if (!show || !canCreate) closeFab();
        document.body.classList.toggle('to-nav-on', show);
        document.body.classList.toggle('to-nav-side', show && isDesktop());
        /* Pe mobil bara e orizontală: starea „pliat" nu se aplică. */
        if (!isDesktop()) document.body.classList.remove('to-side-collapsed');
        else applySideCollapsed();
    }

    /* ======================================================================
       7. NAVIGARE
       ====================================================================== */

    /* ──────────────────────────────────────────────────────────────────────
       GARDA „MODIFICARI NESALVATE" LA IESIREA DIN PROFIL   (§8.2, pasul 3b)
       ----------------------------------------------------------------------
       RO: garda sta in IMPACHETATORUL lui `switchTab` (`wrapSwitchTab`, mai jos), NU in
       `goTab`. Motivul, gasit enumerand apelantii: din profil se poate pleca si prin
       butonul plutitor (`_navNewSession`), prin clopotelul de notificari (`openNotif`),
       prin comutatorul de club (`switchClub`), printr-o notificare push si prin butonul
       inapoi — toate cheama `switchTab` DIRECT si ar ocoli o garda pusa in `goTab`.
       `switchTab` e punctul unic prin care trec toate.

       ⚠ Verificam explicit ca esti CHIAR in tabul de profil. Taburile sunt div-uri
       ascunse, nu distruse: formularul de profil ramane in DOM si dupa ce pleci, iar
       reperul ramane fixat. Fara aceasta conditie, dupa o iesire cu „Nu salva" ai fi
       intrebat la FIECARE navigare ulterioara, intre taburi care n-au nicio legatura
       cu profilul. La revenirea in profil, `switchTab` cheama `renderMyProfile()`,
       care reface formularul si refixeaza reperul — deci ciclul se inchide curat.

       ⚠ `force` exista pentru apelurile PROGRAMATICE: `enforceCurrentTab` te scoate din
       tab cand iti scade rolul, iar acolo „Ramai aici" ar fi o promisiune pe care
       aplicatia nu o poate tine. Acelea nu intreaba.

       EN: the guard lives in the switchTab wrapper, the single choke point every caller
       goes through. It only fires when you are actually on the profile tab (tabs are
       hidden, not destroyed), and never for programmatic navigation.
       ────────────────────────────────────────────────────────────────────── */
    var _pfGuardBusy = false;     /* dialog deschis: un al doilea tap nu deschide al doilea */
    var _pfGuardBypass = false;   /* navigare programatica sau re-apelul de dupa dialog */
    var _pfPendingAfter = null;   /* ce trebuie rulat DUPA ce navigarea chiar are loc */

    /* Verificarile comune, FARA conditia de tab: profilul-modal se poate deschide de pe
       orice tab si chiar din hub, unde `currentTab` nu are nicio legatura cu el. */
    function _profileGuardCommon() {
        try {
            if (_pfGuardBypass) return false;
            if (typeof window.profileFormIsDirty !== 'function') return false;
            if (typeof window.confirmModal3 !== 'function') return false;
            return !!window.profileFormIsDirty();
        } catch (e) { warn('profileGuard', e); return false; }
    }

    function _profileGuardNeeded(tabTinta) {
        try {
            if (_pfGuardBypass) return false;
            /* ⚠ `_pfGuardBusy` NU se verifica aici. A fost o greseala reala: pagina are
               DOUA ascultatoare de `hashchange` (app-core.js:2941 si
               supabase-integration.js:16167). Primul deschidea dialogul si ridica steagul,
               iar al doilea, gasind steagul ridicat, trecea de garda si schimba tabul pe
               sub dialog — asa ca „Ramai aici" te lasa in alt tab. Steagul opreste doar
               DESCHIDEREA unui al doilea dialog, nu si blocarea navigarii.
               EN: busy must suppress a second dialog, never allow navigation through. */
            if (CURTAB() !== 'profile') return false;
            if (tabTinta === 'profile') return false;      /* ramai unde esti: nu pleci nicaieri */
            return _profileGuardCommon();
        } catch (e) { warn('profileGuard', e); return false; }
    }

    /* Garda, expusa pentru codul din afara acestui fisier (profilul-MODAL, in
       `supabase-integration.js`). Intoarce `true` daca a interceptat — caz in care
       apelantul NU trebuie sa continue; plecarea se face din dialog, prin `continua`.
       Expusa ca sa NU existe o a doua implementare a aceleiasi reguli: doua copii ar
       diverge, iar una dintre ele ar ramane in urma.
       EN: exported so the profile modal reuses this exact guard instead of copying it. */
    window._navGuardProfileExit = function (continua) {
        try {
            if (typeof continua !== 'function') return false;
            if (!_profileGuardCommon()) return false;
            if (!_pfGuardBusy) _profileGuardAsk(continua);
            return true;
        } catch (e) { warn('profileGuard.exit', e); return false; }
    };

    /* Butonul inapoi schimba adresa INAINTE ca `hashchange` sa ne anunte. Daca omul alege
       sa ramana, adresa trebuie pusa la loc: altfel bara arata alt tab decat ecranul, iar
       un al doilea „inapoi" sare mai departe decat te-ai astepta.
       ⚠ `replaceState`, NU `location.hash = ...`. A doua varianta ar declansa un nou
       `hashchange` -> `switchTab('profile')` -> `renderMyProfile()`, care reface formularul
       din baza de date si ar STERGE exact modificarile pe care dialogul tocmai le-a salvat
       de la pierdere. Adica „Ramai aici" ar distruge ce ai scris.
       ⚠ Pretul, asumat: intrarea curenta din istoric e rescrisa, deci acel pas inapoi se
       pierde. Mai bine un istoric cu o intrare mai putin decat text pierdut.
       EN: restore the address bar with replaceState; location.hash would re-render and wipe. */
    function _pfRestoreHash() {
        try {
            var tabCurent = CURTAB();
            if (!tabCurent) return;
            var hashAcum = (window.location.hash || '').replace(/^#/, '');
            if (hashAcum === tabCurent) return;   /* adresa e deja corecta (navigare din meniu) */
            if (!window.history || typeof window.history.replaceState !== 'function') return;
            window.history.replaceState(null, '', '#' + tabCurent);
        } catch (e) { warn('profileGuard.hash', e); }
    }

    /* `continua` = ce se intampla daca omul alege sa plece. Primeste o functie, nu un tab,
       ca aceeasi garda sa acopere si `switchTab`, si iesirea catre hub prin logo. */
    /* Dialogul, GENERALIZAT. Primeste ce inseamna „modificat", cum se salveaza si ce se
       intampla daca omul alege sa plece — ca sa serveasca si profilul, si setarile clubului,
       fara doua copii ale aceleiasi reguli care ar diverge la prima corectie.
       `laRamanere` e optional si exista pentru ce e specific navigarii (adresa din bara,
       actiunea amanata); la un modal nu are ce face si nu se transmite.
       EN: one dialog, parameterised by "is dirty", "save" and "proceed". */
    function _guardAsk(cfg) {
        _pfGuardBusy = true;
        var elibereaza = function () { _pfGuardBusy = false; };
        var mergiMaiDeparte = function () {
            _pfGuardBypass = true;
            try { cfg.continua(); }
            catch (e) { warn('guard.go', e); }
            finally { _pfGuardBypass = false; }
        };
        window.confirmModal3().then(function (raspuns) {
            if (raspuns === 'stay') {
                elibereaza();
                if (typeof cfg.laRamanere === 'function') { try { cfg.laRamanere(); } catch (e) { warn('guard.stay', e); } }
                return;
            }
            if (raspuns === 'discard') { elibereaza(); mergiMaiDeparte(); return; }
            /* „Salveaza": daca salvarea NU trece (nume gol, retea cazuta), RAMANEM pe loc.
               Omul a cerut „salveaza si pleaca"; a pleca fara sa se fi salvat ar fi exact
               minciuna pe care dialogul trebuia s-o previna.
               Verificarea e pe rezultatul REAL: dupa o salvare reusita formularul nu mai e
               „modificat", fiindca reperul lui a fost refixat. */
            Promise.resolve(typeof cfg.salveaza === 'function' ? cfg.salveaza() : null)
                .then(function () {
                    elibereaza();
                    var incaModificat = true;
                    try { incaModificat = !!cfg.esteModificat(); } catch (e) { warn('guard.after', e); }
                    if (incaModificat) return;          /* salvarea a esuat -> ramanem */
                    mergiMaiDeparte();
                })
                .catch(function (e) { elibereaza(); warn('guard.save', e); });
        }).catch(function (e) { elibereaza(); warn('guard', e); });
    }

    /* Varianta pentru NAVIGARE (profil-tab): pe langa dialog, are grija de adresa din bara
       si de actiunea amanata de `goTab`. */
    function _profileGuardAsk(continua) {
        _guardAsk({
            esteModificat: function () { return window.profileFormIsDirty(); },
            salveaza: function () { return window.updateMyProfile ? window.updateMyProfile() : null; },
            continua: continua,
            laRamanere: function () {
                _pfRestoreHash();
                /* ⚠ Anulam si actiunea de dupa navigare. Altfel ar ramane in asteptare si
                   ar porni la URMATOAREA navigare, complet nelegata — de exemplu un camp
                   de cautare completat dintr-o cautare pe care ai abandonat-o. */
                _pfPendingAfter = null;
            }
        });
    }

    /* Garda GENERICA, expusa pentru formularele din afara acestui fisier. Intoarce `true`
       daca a interceptat — caz in care apelantul NU trebuie sa continue; plecarea se face
       din dialog, prin `continua`.
       EN: exported generic guard; returns true when it intercepted. */
    window._navGuardUnsaved = function (esteModificat, salveaza, continua) {
        try {
            if (typeof esteModificat !== 'function' || typeof continua !== 'function') return false;
            if (_pfGuardBypass) return false;
            if (typeof window.confirmModal3 !== 'function') return false;
            if (!esteModificat()) return false;
            if (!_pfGuardBusy) _guardAsk({ esteModificat: esteModificat, salveaza: salveaza, continua: continua });
            return true;   /* blocam si cand exista deja un dialog deschis */
        } catch (e) { warn('guardUnsaved', e); return false; }
    };


    function goTab(tab, opts) {
        try {
            if (typeof window.switchTab !== 'function') return;
            /* `after` nu se ruleaza aici: garda poate amana navigarea cu cateva secunde.
               Il lasam in asteptare, iar impachetatorul il porneste cand tabul chiar
               s-a schimbat. EN: deferred until navigation actually happens. */
            _pfPendingAfter = (opts && typeof opts.after === 'function') ? opts.after : null;
            if (opts && opts.force) {
                /* Iesire PROGRAMATICA (pierderea accesului la tab): nu intrebam, fiindca
                   „Ramai aici" n-ar avea ce sa insemne. */
                _pfGuardBypass = true;
                try { window.switchTab(tab); } finally { _pfGuardBypass = false; }
            } else {
                window.switchTab(tab);
            }
        } catch (e) { warn('goTab', e); }
    }

    function runFn(name) {
        try {
            var f = window[name];
            if (typeof f !== 'function') { warn('runFn', new Error('lipsește funcția ' + name)); return; }
            f();
        } catch (e) { warn('runFn:' + name, e); }
    }

    function findRow(hubId, rowId) {
        var def = MENU.hubs[hubId];
        if (!def) return null;
        for (var i = 0; i < def.groups.length; i++) {
            var rows = def.groups[i].rows;
            for (var j = 0; j < rows.length; j++) if (rows[j].id === rowId) return rows[j];
        }
        return null;
    }

    /* ⚠ A7 RETRAS (iulie 2026), la cerere, după folosire reală.
       Specificația inițială cerea ca bara să te ducă înapoi la ULTIMA poziție
       din hub („ieși din Club fiind pe Catalog, revii în Club, ești tot pe
       Catalog"). În practică s-a dovedit deranjant: odată intrat în Chat, orice
       revenire la Club te ducea tot în Chat, inclusiv după ce ieșiseși din
       secțiune prin bara „← Club". Aveai nevoie de meniu și primeai o pagină.

       Regula nouă, mai simplă și previzibilă: **o destinație din bară deschide
       MEREU lista hub-ului**. Într-o pagină ajungi doar dacă apeși tu rândul.
       Am șters memoria în loc s-o „curăț la ieșire", pentru că, odată curățată
       la ieșire, n-ar mai fi fost folosită niciodată — ar fi rămas mecanism mort.
       Memoria pentru sidebar (pliat/desfăcut) NU e afectată: aceea e o preferință
       de dispozitiv și rămâne în `state.nav.sidebarCollapsed`.
       EN: the "return to last position" behaviour was removed on request — a bar
       destination always opens the hub list; you land on a page only by tapping
       its row. The sidebar collapse memory is unaffected. */
    function goBarDestination(bar) {
        _cameFrom = null;
        goTab(bar.tab);
    }

    function activateRow(hubId, rowId, srcEl) {
        var row = findRow(hubId, rowId);
        if (!row) return;
        /* Re-verificăm capabilitatea la CLICK, nu doar la randare: dacă rolul a
           scăzut între timp, un rând rămas pe ecran nu trebuie să facă nimic.
           (Garda finală rămâne în switchTab / funcția țintă / RLS.) */
        if (!rowVisible(row)) {
            try { window.showNotification(T('rbac.noPermission', 'Nu ai permisiunea pentru această acțiune.'), 'error'); } catch (e) { warn('activateRow', e); }
            return;
        }
        _cameFrom = hubId;
        if (row.go && row.go.tab) {
            goTab(row.go.tab);
            if (row.after || row.openAcc) {
                setTimeout(function () {
                    try {
                        /* Secțiunile din Admin sunt acordeoane închise implicit.
                           Fără asta, „Moderare" te ducea în tabul Admin lângă o
                           secțiune pliată, care părea că nu există. */
                        if (row.openAcc) {
                            var body = byId(row.openAcc);
                            if (body && body.classList.contains('hidden')) {
                                var head = body.previousElementSibling;
                                if (head && typeof window.toggleTilePanel === 'function') {
                                    window.toggleTilePanel(row.openAcc, head);
                                }
                            }
                        }
                        var el = byId(row.after || row.openAcc);
                        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (e) { warn('scrollAfter', e); }
                }, 260);
            }
        } else if (row.go && row.go.fn) {
            runFn(row.go.fn);
            /* ⚠ Popup-urile de header (`onlinePanel`, `settingsPanel`) se ancorau
               sub butonul lor din bara de sus. La Etapa 1b butoanele acelea sunt
               ascunse, iar `getBoundingClientRect()` pe un element `display:none`
               întoarce numai zerouri => popup-ul ar sări în colțul stânga-sus.
               Le re-ancorăm sub RÂNDUL apăsat. Pe mobil `anchorPopup` centrează
               oricum, deci apelul e inofensiv.
               EN: re-anchor header popups to the tapped row, because their
               original trigger buttons are now hidden (zero-sized rect). */
            if (row.panel) {
                setTimeout(function () {
                    try {
                        var p = byId(row.panel);
                        if (p && !p.classList.contains('hidden') && typeof window.anchorPopup === 'function') {
                            /* Ancorare la RÂND ar așeza panoul oriunde e rândul pe
                               ecran — inclusiv în colțul din dreapta-jos, sub FAB.
                               Trecem `null`: `anchorPopup` folosește atunci ramura
                               „centrat, sus", predictibilă și identică pe mobil și
                               pe desktop. `clampPopup` corectează restul. */
                            window.anchorPopup(p, null);
                        }
                    } catch (e) { warn('reanchor:' + row.panel, e); }
                }, 0);
            }
        }
    }

    /* ======================================================================
       8. DELEGARE DE EVENIMENTE (§35.1 — zero onclick inline)
       ====================================================================== */

    function onDocClick(e) {
        var el = (e.target && e.target.closest)
            ? e.target.closest('[data-nav-bar],[data-nav-row],[data-nav-create],[data-nav-back],[data-nav-side],[data-nav-qi],[data-nav-qclose],#toSearchBtn')
            : null;

        /* Click în afara panoului de căutare îl închide. */
        var qp = byId('toSearchPanel');
        if (qp && !qp.classList.contains('hidden')) {
            var inQ = e.target && e.target.closest && e.target.closest('#toSearchPanel, #toSearchBtn');
            if (!inQ) closeSearch();
        }

        /* Click în afara meniului FAB îl închide. */
        if (_fabOpen) {
            var inMenu = e.target && e.target.closest && e.target.closest('#toFabMenu, #toFab');
            if (!inMenu) closeFab();
        }
        /* Plasă de siguranță pentru tranziția de rol: dacă vreo cale de realtime
           ne scapă, la PRIMA interacțiune a userului îl mutăm de pe o pagină la
           care nu mai are drept. Fără listener nou (refolosim acesta), fără
           timer, fără query. */
        try { enforceCurrentTab(); } catch (e2) { warn('clickEnforce', e2); }

        if (!el) return;

        var v, i, k;
        if (el.id === 'toSearchBtn') {
            e.preventDefault();
            var pnl = byId('toSearchPanel');
            if (pnl && !pnl.classList.contains('hidden')) closeSearch(); else openSearch();
            return;
        }
        if (el.getAttribute('data-nav-qclose')) { closeSearch(); return; }
        if ((v = el.getAttribute('data-nav-qi')) !== null && v !== undefined && v !== '') {
            var r = _qResults[parseInt(v, 10)];
            if (r) runResult(r);
            return;
        }
        if (el.getAttribute('data-nav-side')) {
            setSideCollapsed(!sideCollapsed());
            applySideCollapsed();
            renderBar();
            return;
        }
        if ((v = el.getAttribute('data-nav-bar'))) {
            for (i = 0; i < MENU.bar.length; i++) {
                if (MENU.bar[i].id === v) { closeFab(); goBarDestination(MENU.bar[i]); break; }
            }
            return;
        }
        if ((v = el.getAttribute('data-nav-row'))) {
            activateRow(el.getAttribute('data-nav-hub') || '', v, el);
            return;
        }
        if ((v = el.getAttribute('data-nav-back'))) {
            _cameFrom = null;
            goTab(v);
            return;
        }
        if ((v = el.getAttribute('data-nav-create'))) {
            closeFab();
            for (k = 0; k < MENU.create.length; k++) {
                if (MENU.create[k].id === v && MENU.create[k].go && MENU.create[k].go.fn) { runFn(MENU.create[k].go.fn); break; }
            }
            return;
        }
    }

    function onFabClick(e) { e.stopPropagation(); toggleFab(); }

    function onKeyDown(e) {
        if (e.key === 'Escape' && _fabOpen) closeFab();

        var qp = byId('toSearchPanel');
        var qOpen = !!(qp && !qp.classList.contains('hidden'));

        /* Ctrl/Cmd+K deschide căutarea, ca în orice aplicație. */
        if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
            if (appVisible()) { e.preventDefault(); openSearch(); }
            return;
        }
        if (!qOpen) return;

        if (e.key === 'Escape') { e.preventDefault(); closeSearch(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); moveQ(1); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); moveQ(-1); return; }
        if (e.key === 'Enter') {
            var items = document.querySelectorAll('#toSearchPanel .to-q-item');
            var pick = (_qIndex >= 0 && items[_qIndex]) ? items[_qIndex] : items[0];
            if (pick) { e.preventDefault(); pick.click(); }
        }
    }

    /* ======================================================================
       9. CABLARE
       ----------------------------------------------------------------------
       Wrapper-e ADITIVE. Nu înlocuiesc nimic: apelează originalul, apoi
       randează. Același pattern cu `tournaments.js` (care înfășoară deja
       `switchTab` pentru închiderea modalelor de turnee).
       ====================================================================== */

    /* ======================================================================
       9b. TRANZIȚIA DE ROL LA CALD ȘI COMUTAREA DE CLUB (A9.1)
       ----------------------------------------------------------------------
       Rolul se poate schimba cât ești logat (realtime), iar clubul se poate
       comuta din header. În ambele cazuri hub-urile trebuie reordonate FĂRĂ
       reload, iar dacă eram pe o destinație la care nu mai am drept trebuie să
       cad silențios pe „Acasă".
       NU adăugăm triggere noi (§36): ne agățăm de `render()`, care rulează deja
       pe cascada de realtime și conține `applyRoleUIGating()`.
       ====================================================================== */

    var _lastCtx = null;          /* "clubId|rol" la ultima randare */
    var _lastGateCtx = null;      /* idem, pentru re-aplicarea gărzilor din DOM */
    var _origRoleGating = null;   /* `applyRoleUIGating` NEîmpachetat */
    var _fallbackBusy = false;

    function ctxKey() {
        var club = (typeof currentClubId !== 'undefined') ? String(currentClubId || '') : '';
        var role = '';
        try { if (typeof window.currentClubMyRole === 'function') role = String(window.currentClubMyRole() || ''); }
        catch (e) { warn('ctxKey', e); }
        return club + '|' + role;
    }

    /* Dacă tabul curent nu mai e permis, revenim pe Dashboard — o singură dată,
       fără notificare zgomotoasă (spec: „fallback silențios"). */
    function enforceCurrentTab() {
        if (_fallbackBusy) return;
        var tab = CURTAB();
        if (!tab || tab === 'dashboard' || MENU.hubs[tab]) return;

        /* ⚠ Nu evacuăm userul cât timp rolul încă nu e cunoscut (în timpul
           hidratării `state.myClubs` e gol o fracțiune de secundă și ORICE tab
           ar părea interzis). Verificăm doar când avem un rol rezolvat. */
        var st = ST();
        if (!st || !st.currentUser) return;
        var role = null;
        try { role = window.currentClubMyRole ? window.currentClubMyRole() : null; } catch (e) { return; }
        if (!role && !IS_OWNER()) return;

        /* ⚠ `switchTab` are o EXCEPȚIE explicită (§2): tabul „Sesiune" poate fi
           deschis în mod doar-vizualizare de un membru care urmărește o sesiune
           live la care participă. Verificarea mea o ignora, deci l-ar fi aruncat
           pe Acasă în timpul sesiunii. Respectăm aceeași excepție.
           EN: honour the read-only live-session exception that `switchTab` has. */
        if (tab === 'session' && window._liveReadOnly === true) return;

        var allowed = true;
        try {
            if (typeof window.canAccessTab === 'function') allowed = !!window.canAccessTab(tab);
        } catch (e) { warn('enforceCurrentTab', e); return; }
        if (allowed) return;

        _fallbackBusy = true;
        /* ⚠ `force`: iesire PROGRAMATICA, provocata de pierderea accesului la tab.
           Aici „Ramai aici" n-ar avea ce sa insemne, deci nu intrebam. */
        try { goTab('dashboard', { force: true }); } finally {
            setTimeout(function () { _fallbackBusy = false; }, 0);
        }
    }

    /* Reîmprospătarea numărului de cereri, declanșată de o ACȚIUNE a userului
       (deschiderea unui hub), nu de un timer. Strangulată la 30s, ca deschiderile
       repetate să nu producă un query de fiecare dată. NU e polling și NU e un
       abonament realtime nou (§36).
       EN: user-initiated refresh of the pending-join count, throttled to 30s.
       Not polling, no new realtime channel. */
    var _pendingLastFetch = 0;
    function maybeRefreshPending() {
        var now = Date.now();
        if (now - _pendingLastFetch < 30000) return;

        /* O singură strangulare pentru AMBELE numărători, ca să nu se înmulțească
           interogările pe măsură ce apar indicatoare noi. */
        var jobs = [];
        if (CAN('member.manage') && typeof window.refreshPendingBadge === 'function') {
            try { jobs.push(window.refreshPendingBadge()); } catch (e) { warn('maybeRefreshPending.join', e); }
        }
        if (CAN('moderation.view') && typeof window.refreshOpenReportsBadge === 'function') {
            try { jobs.push(window.refreshOpenReportsBadge()); } catch (e) { warn('maybeRefreshPending.rep', e); }
        }
        if (!jobs.length) return;
        _pendingLastFetch = now;

        var redraw = function () {
            try {
                var tab = CURTAB();
                if (MENU.hubs[tab]) renderHub(tab);
            } catch (e) { warn('maybeRefreshPending.render', e); }
        };
        jobs.forEach(function (p) {
            if (p && typeof p.then === 'function') p.then(redraw, function () { /* rămâne numărul vechi */ });
        });
    }

    function refresh() {
        try {
            ensureNodes();
            applyVisibility();
            renderBar();
            var tab = CURTAB();
            if (MENU.hubs[tab]) { renderHub(tab); _cameFrom = null; maybeRefreshPending(); }
            renderBackBar();
            clearStaleFilter();
            syncHeaderRole();
            placeLegalFooter();
            /* Butonul „Fă sesiunea live" din tabul Sesiune depinde de starea
               formularului (sesiune nouă vs. sesiune live deschisă). Funcția
               trăiește în app-modules-1.js; o rechemăm la orice navigare, ca să
               fie corect și după închiderea unei sesiuni live. */
            try { if (typeof window.syncGoLiveBtn === 'function') window.syncGoLiveBtn(); }
            catch (e) { warn('syncGoLiveBtn', e); }

            /* ⚠ CORECȚIE: secțiunile marcate cu `data-cap` din paginile existente
               (Moderare, Uniformizare scoruri etc.) apăreau doar după un refresh
               al paginii. Cauza: `applyRoleUIGating()` — singura funcție care le
               arată/ascunde — e apelată DOAR din `render()`. La login, ea rulează
               înainte ca `state.myClubs` să fie populat, deci `can()` întoarce
               `false` și secțiunile rămân ascunse cu `display:none`; nimic nu o
               mai reia. O reaplicăm noi când se schimbă contextul (rol sau club),
               adică exact momentul în care răspunsul lui `can()` se poate schimba.
               Apelăm ORIGINALUL, nu versiunea împachetată, ca să nu recursăm.
               EN: re-apply the DOM capability gating when role/club context
               changes; `render()` alone runs it too early at login. */
            var gk = ctxKey();
            if (gk !== _lastGateCtx) {
                _lastGateCtx = gk;
                try { if (_origRoleGating) _origRoleGating(); } catch (e) { warn('reGating', e); }
            }

            /* Reordonare la schimbarea de rol sau de club.
               ⚠ Corecție: verificarea NU se mai face doar când se schimbă cheia
               de context. Realtime-ul poate actualiza rolul fără să treacă prin
               `render()`, iar userul rămânea pe o pagină interzisă până la
               următoarea navigare. Verificăm la fiecare `refresh()` — costă un
               singur `canAccessTab()`, adică o citire de atribut din DOM. */
            _lastCtx = ctxKey();
            enforceCurrentTab();
        } catch (e) { warn('refresh', e); }
    }

    /* ---------------------------------------------------------------------
       UNDE TE LASĂ APLICAȚIA CÂND INTRI ÎNTR-UN TAB
       ---------------------------------------------------------------------
       RO: Comutarea de tab nu resetează derularea paginii, așa că intrai uneori
           direct la mijlocul sau la finalul unei liste lungi — pentru că acolo
           rămăseseși în tabul precedent. Ducem pagina sus la FIECARE schimbare
           reală de tab (nu și la simple re-randări).
           Excepție deliberată: CHATUL. Acolo ultimul mesaj e cel care contează,
           deci containerul lui se duce la FINAL. `loadChat()` face asta doar când
           sosește un mesaj NOU (`lastId !== prevLastId`), nu și la intrarea în
           tab — de aceea o completăm aici.
           Sunt două elemente diferite (pagina și `#chatMessages`), deci cele două
           derulări nu se bat cap în cap.
       EN: reset page scroll on every real tab change; the chat container instead
           scrolls to the newest message, which `loadChat()` only did when a new
           message arrived.
       --------------------------------------------------------------------- */
    function landOnTab(tab, changed) {
        if (!changed) return;
        try { window.scrollTo(0, 0); } catch (e) { warn('landOnTab.window', e); }
        try { if (document.scrollingElement) document.scrollingElement.scrollTop = 0; }
        catch (e) { warn('landOnTab.doc', e); }

        if (tab === 'chat') {
            /* v9 8.1-c2a: cu mesaje necitite, chat-core.js poziționează la primul
               necitit (ca în WhatsApp) în locul capătului. EN: land on first unread. */
            try { if (window.TO_CHAT && window.TO_CHAT.club && typeof window.TO_CHAT.club.land === 'function' && window.TO_CHAT.club.land()) return; }
            catch (e) { warn('landOnTab.unread', e); }
            /* După ce `loadChat()` a umplut containerul. Două încercări: una
               imediat, una după randare, ca să prindem și cazul lent. */
            var toBottom = function () {
                try {
                    var c = byId('chatMessages');
                    if (c) c.scrollTop = c.scrollHeight;
                } catch (e) { warn('landOnTab.chat', e); }
            };
            toBottom();
            setTimeout(toBottom, 80);
        }
    }

    /* Iesirea catre hub prin click pe numele aplicatiei. `goHome()` cheama `showClubHub()`
       si NU atinge `switchTab`, deci ocolea complet garda: plecai din profil cu modificari
       nesalvate fara sa fii intrebat. Impachetam si aici, dupa acelasi tipar.
       ⚠ Impachetam `goHome`, NU `showClubHub`: acesta din urma mai e chemat la pornire si
       dupa parasirea unui club, unde un dialog ar fi absurd. `goHome` e singura cale
       pornita de utilizator. EN: the logo exits to the hub without touching switchTab. */
    function wrapGoHome() {
        if (window.__toNavGoHomeWrapped) return true;
        if (typeof window.goHome !== 'function') return false;
        var orig = window.goHome;
        window.goHome = function () {
            var self = this, argumente = arguments;
            if (_profileGuardNeeded(null)) {
                if (!_pfGuardBusy) {
                    _profileGuardAsk(function () { orig.apply(self, argumente); });
                }
                return undefined;
            }
            return orig.apply(self, argumente);
        };
        window.__toNavGoHomeWrapped = true;
        return true;
    }

    function wrapSwitchTab() {
        if (window.__toNavSwitchWrapped) return true;
        if (typeof window.switchTab !== 'function') return false;
        var orig = window.switchTab;
        window.switchTab = function (tabTinta) {
            var before = CURTAB();
            /* GARDA „modificari nesalvate" (§8.2, pasul 3b-bis). Aici, si nu in `goTab`,
               fiindca acesta e SINGURUL punct prin care trec toti apelantii: meniul,
               butonul plutitor, clopotelul, comutatorul de club, push, `hashchange`.
               ⚠ Iesim FARA sa chemam functia reala: navigarea se reia din dialog, cu
               aceleasi argumente si cu garda ocolita. EN: single choke point guard. */
            if (_profileGuardNeeded(tabTinta)) {
                /* Blocam navigarea IN AMBELE cazuri; deschidem dialogul doar daca nu e
                   deja unul pe ecran (al doilea ascultator de `hashchange`, tap dublu). */
                var argumente = arguments;
                if (!_pfGuardBusy) {
                    _profileGuardAsk(function () { window.switchTab.apply(null, argumente); });
                }
                return undefined;
            }
            var r = orig.apply(this, arguments);
            try {
                closeFab();
                refresh();
                var after = CURTAB();
                landOnTab(after, after !== before);
                /* Actiunea amanata de `goTab` (ex. completarea campului de cautare) ruleaza
                   DOAR daca tabul chiar s-a schimbat in cel cerut. */
                if (_pfPendingAfter && after === tabTinta) {
                    var dupa = _pfPendingAfter; _pfPendingAfter = null;
                    try { dupa(); } catch (e) { warn('switchTab.after', e); }
                }
            } catch (e) { warn('switchTab.after', e); }
            return r;
        };
        window.__toNavSwitchWrapped = true;
        return true;
    }

    /* ⚠ CAUZA REALĂ a retrogradării care nu muta userul (găsită la Etapa 2c):
       `applyRoleUIGating()` este apelată dintr-un SINGUR loc — `render()`,
       app-core.js:1966. Iar calea prin care rolul se schimbă în timp real,
       `scheduleMyClubsReload()` (supabase-integration.js:6188), apelează
       `renderClubSwitcher()`, `renderClubHub()` și `applyClubRole()`, dar NU
       apelează `render()`. Rezultat: rolul se schimba în stare (de aceea
       acțiunile erau respinse), dar nici re-gating-ul UI, nici cârligele mele
       nu rulau. Wrapper-ul pe `applyRoleUIGating` era corect, dar inutil pe
       exact scenariul care ne interesa.
       Cârligul corect este `applyClubRole()`: e apelat de TOATE căile care
       schimbă rolul, inclusiv de cea din realtime.
       EN: the realtime path never calls `render()`, so hooking
       `applyRoleUIGating` was useless. `applyClubRole()` is the function every
       role-changing path goes through. */
    function wrapRoleGating() {
        if (window.__toNavGatingWrapped) return true;
        var okA = false, okB = false;

        if (typeof window.applyClubRole === 'function' && !window.__toNavClubRoleWrapped) {
            var origA = window.applyClubRole;
            window.applyClubRole = function () {
                var r = origA.apply(this, arguments);
                try { refresh(); } catch (e) { warn('applyClubRole.after', e); }
                return r;
            };
            window.__toNavClubRoleWrapped = true;
        }
        okA = !!window.__toNavClubRoleWrapped;

        if (typeof window.applyRoleUIGating === 'function' && !window.__toNavGatingOnly) {
            var origB = window.applyRoleUIGating;
            _origRoleGating = origB;          /* păstrăm originalul: îl apelăm din
                                                 `refresh()` FĂRĂ să reintrăm în wrapper */
            window.applyRoleUIGating = function () {
                var r = origB.apply(this, arguments);
                try { refresh(); } catch (e) { warn('roleGating.after', e); }
                return r;
            };
            window.__toNavGatingOnly = true;
        }
        okB = !!window.__toNavGatingOnly;

        if (okA && okB) { window.__toNavGatingWrapped = true; return true; }
        return false;
    }

    function wrapRender() {
        if (window.__toNavRenderWrapped) return true;
        if (typeof window.render !== 'function') return false;
        var orig = window.render;
        window.render = function () {
            var r = orig.apply(this, arguments);
            try { refresh(); } catch (e) { warn('render.after', e); }
            return r;
        };
        window.__toNavRenderWrapped = true;
        return true;
    }

    /* ---------------------------------------------------------------------
       CORECȚIE: popup-urile de header ieșeau din ecran pe desktop.
       `anchorPopup()` (supabase-integration.js) calculează poziția folosind
       `window.innerWidth`, care INCLUDE lățimea barei de derulare (~15px), și
       nu remăsoară panoul după ce l-a poziționat. Rezultat: marginea din dreapta
       ajungea sub scrollbar și conținutul apărea tăiat.
       Nu modificăm `anchorPopup` — îl ÎNVELIM și corectăm poziția după el,
       măsurând cu `documentElement.clientWidth/Height` (FĂRĂ scrollbar).
       EN: wrap `anchorPopup` and clamp the panel into the visual viewport after
       it positions itself; the original uses `innerWidth` (includes scrollbar).
       --------------------------------------------------------------------- */
    /* ⚠ CAUZA REALĂ (identificată la Etapa 2, corecție):
       `.app-header` are `backdrop-filter: blur(14px)`. Conform specificației CSS,
       un element cu `filter`/`backdrop-filter`/`transform` devine BLOC CONȚINĂTOR
       pentru descendenții `position: fixed`. Cele patru popup-uri (`notifPanel`,
       `clubMenu`, `onlinePanel`, `settingsPanel`) sunt COPII ai headerului, deci
       `left`/`top` NU sunt coordonate de ecran, ci relative la caseta headerului.
       `anchorPopup()` calculează însă în coordonate de ECRAN (`getBoundingClientRect`
       + `window.innerWidth`). Diferența = distanța de la marginea ecranului la
       marginea headerului. Bug-ul exista dinainte (`.container` e centrat cu
       `margin: 0 auto`), dar `padding-left`-ul sidebar-ului l-a mărit vizibil.

       Nu mutăm popup-urile din header (ar afecta `closeHeaderPopups` și CSS-ul
       existent) și nu scoatem `backdrop-filter` (e branding). Corectăm pe DELTĂ:
       măsurăm cât iese din ecran și deplasăm cu exact atât. Fiind o diferență,
       merge indiferent de sistemul de coordonate — și rămâne corect dacă cineva
       schimbă mai târziu structura.
       EN: `.app-header` has `backdrop-filter`, so it is the containing block for
       its `position:fixed` children. `anchorPopup` computes viewport coords.
       We correct by DELTA, which is frame-agnostic. */
    /* ⚠ SOLUȚIA STRUCTURALĂ pentru popup-urile de header (înlocuiește peticele
       anterioare).
       Problema de fond avea două straturi:
       (a) cele patru panouri erau COPII ai `<header class="app-header">`, care are
           `backdrop-filter` => devine bloc conținător pentru `position: fixed`,
           deci `left`/`top` nu erau coordonate de ecran;
       (b) `.app-header` are și `position: sticky` + `z-index: 200` => context de
           stivuire, deci `z-index: 9999` al panourilor nu depășea FAB-ul.
       Ambele dispar dacă panourile stau direct în `<body>`. Le MUTĂM o singură
       dată, la inițializare. Niciun selector CSS nu depinde de poziția lor în
       header (verificat), iar `closeHeaderPopups()` le caută după id.

       Peste asta punem O SINGURĂ regulă de așezare, identică pentru toate patru,
       în locul a trei comportamente diferite (sub clopoțel / sub chip / lângă
       rândul apăsat): panoul apare mereu SUB HEADER, aliniat la marginea din
       dreapta a zonei de conținut pe desktop, și CENTRAT pe mobil.
       EN: move the four header popups into <body> (kills both the containing-block
       and the stacking-context problems) and give them one consistent placement. */

    function relocatePopups() {
        POPUPS.forEach(function (id) {
            var el = byId(id);
            if (el && el.parentNode !== document.body) {
                try { document.body.appendChild(el); } catch (e) { warn('relocatePopups:' + id, e); }
            }
        });
    }

    function placeHeaderPopup(panel) {
        if (!panel || panel.classList.contains('hidden')) return;
        var M = 12;
        var vw = document.documentElement.clientWidth;
        var vh = document.documentElement.clientHeight;

        panel.style.position = 'fixed';
        panel.style.transform = 'none';
        panel.style.right = 'auto';
        panel.style.maxWidth = '';

        var hdr = byId('appHeader');
        var hr = hdr ? hdr.getBoundingClientRect() : null;
        var top = hr ? Math.max(M, hr.bottom + 8) : 64;

        var w = panel.offsetWidth || 320;
        if (w > vw - 2 * M) { w = vw - 2 * M; panel.style.maxWidth = w + 'px'; }

        var left;
        if (vw <= 768 || !hr) left = Math.round((vw - w) / 2);
        else left = Math.round(hr.right - w);
        if (left > vw - M - w) left = vw - M - w;
        if (left < M) left = M;

        panel.style.left = left + 'px';
        panel.style.top = Math.round(top) + 'px';
        panel.style.maxHeight = Math.max(180, Math.round(vh - top - M)) + 'px';
        panel.style.overflowY = 'auto';
    }

    function isHeaderPopup(panel) {
        return !!(panel && panel.id && POPUPS.indexOf(panel.id) !== -1);
    }

    function clampPopup(panel) {
        if (!panel || panel.classList.contains('hidden')) return;
        var M = 8;
        var vw = document.documentElement.clientWidth;
        var vh = document.documentElement.clientHeight;
        var r = panel.getBoundingClientRect();
        if (!r.width || !r.height) return;

        /* Prea lat pentru ecran => îl îngustăm întâi. */
        if (r.width > vw - 2 * M) {
            panel.style.maxWidth = (vw - 2 * M) + 'px';
            r = panel.getBoundingClientRect();
        }

        var dx = 0;
        if (r.right > vw - M) dx = (vw - M) - r.right;
        else if (r.left < M) dx = M - r.left;
        if (dx) {
            var curL = parseFloat(panel.style.left);
            if (!isFinite(curL)) curL = 0;
            panel.style.left = (curL + dx) + 'px';
            r = panel.getBoundingClientRect();
        }

        var dy = 0;
        if (r.top < M) dy = M - r.top;
        if (dy) {
            var curT = parseFloat(panel.style.top);
            if (!isFinite(curT)) curT = 0;
            panel.style.top = (curT + dy) + 'px';
            r = panel.getBoundingClientRect();
        }
        if (r.bottom > vh - M) {
            panel.style.maxHeight = Math.max(180, vh - r.top - M) + 'px';
            panel.style.overflowY = 'auto';
        }
    }

    function wrapAnchorPopup() {
        if (window.__toNavAnchorWrapped) return true;
        if (typeof window.anchorPopup !== 'function') return false;
        var orig = window.anchorPopup;
        window.anchorPopup = function (panel) {
            /* Pentru cele patru panouri de header NU mai lăsăm calculul original:
               el produce trei așezări diferite, ceea ce arată haotic. Aplicăm
               regula unică. Restul apelanților rămân neatinși. */
            if (isHeaderPopup(panel)) {
                try { placeHeaderPopup(panel); } catch (e) { warn('placeHeaderPopup', e); }
                return;
            }
            var r = orig.apply(this, arguments);
            try { clampPopup(panel); } catch (e) { warn('clampPopup', e); }
            return r;
        };
        window.__toNavAnchorWrapped = true;
        return true;
    }

    /* Retragerea meniului vechi (C6, parțial).
       `ensureQuickSessionFab()` reafișează butonul verde „+" la fiecare
       `showMainApp()`. Îl neutralizăm AICI, nu în app-core.js, ca să nu atingem
       fișierul cel mai riscant. Codul mort din app-core.js se șterge la Etapa 7. */
    function retireOldMenu() {
        try {
            /* FAB-ul verde vechi: nu se mai creează niciodată. */
            window.ensureQuickSessionFab = function () {
                var el = byId('quickSessionFab');
                if (el && el.parentNode) el.parentNode.removeChild(el);
            };
            window.ensureQuickSessionFab();

            /* ⚠ Overlay-ul vechi era șters DOAR o dată, la pornire, dar
               `ensureTabMenu()` (apelată din `showMainApp()`) îl RECREA la fiecare
               autentificare. Nodurile rămâneau în pagină — invizibile, pentru că
               sunt create cu clasa `hidden`, dar prezente.
               Îl retragem la fel ca pe FAB: funcția devine o curățare, nu o creare.
               Am ales varianta asta în locul ștergerii codului din `app-core.js`
               pentru că acolo `showMainApp()` apelează `ensureTabMenu()` FĂRĂ gardă
               de existență — o ștergere greșită ar arunca la fiecare login. Aici
               efectul e identic, iar riscul rămâne izolat într-un singur fișier.
               EN: neutralise `ensureTabMenu` from here instead of deleting it in
               app-core.js, where `showMainApp()` calls it unguarded. */
            window.ensureTabMenu = function () {
                ['tabMenuOverlay', 'tabMenuFabs'].forEach(function (id) {
                    var el = byId(id); if (el && el.parentNode) el.parentNode.removeChild(el);
                });
            };
            window.ensureTabMenu();
        } catch (e) { warn('retireOldMenu', e); }
    }

    /* ⚠ De ce FAB-ul acoperea popup-urile deși are z-index 350, iar ele 9999:
       `.app-header` are `position: sticky` + `z-index: 200`, deci formează un
       CONTEXT DE STIVUIRE propriu. `z-index: 9999` al popup-urilor se aplică
       DOAR în interiorul acelui context; față de restul paginii, tot pachetul
       „header + popup-uri" valorează 200. FAB-ul, aflat direct în `<body>` la
       350, ajunge deasupra. Nu umblăm la z-index-urile existente — respectăm
       regula C3 din brief: FAB-ul SE ASCUNDE, nu se suprapune.
       EN: `.app-header` creates a stacking context (sticky + z-index), so the
       popups' 9999 is scoped inside it; the whole header stack is worth 200.
       Per rule C3 the FAB hides instead of competing. */
    function anyPopupOpen() {
        for (var i = 0; i < POPUPS.length; i++) {
            var el = byId(POPUPS[i]);
            if (el && !el.classList.contains('hidden')) return true;
        }
        return false;
    }

    function syncPopupState() {
        var open = anyPopupOpen();
        document.body.classList.toggle('to-popup-open', open);
        if (open) closeFab();
    }

    function observePopups() {
        if (!window.MutationObserver) return;
        POPUPS.forEach(function (id) {
            var el = byId(id);
            if (!el) return;
            try {
                new MutationObserver(function () {
                    syncPopupState();
                    try { placeHeaderPopup(el); } catch (e) { warn('popupPlace', e); }
                }).observe(el, { attributes: true, attributeFilter: ['class'] });
            } catch (e) { warn('observePopups:' + id, e); }
        });
    }

    function init() {
        ensureNodes();
        ensureSearchPanel();
        retireOldMenu();

        document.addEventListener('click', onDocClick, false);
        document.addEventListener('keydown', onKeyDown, false);
        var fab = byId('toFab');
        if (fab) fab.addEventListener('click', onFabClick, false);
        window.addEventListener('resize', function () {
            applyVisibility();
            /* ⚠ Footerul legal trebuie RE-PLASAT la redimensionare. Fără asta,
               trecând de la desktop la mobil, el rămânea în bara de navigare —
               care pe mobil e orizontală, deci footerul devenea al șaselea
               element și înghesuia cele 5 butoane. */
            placeLegalFooter();
        }, false);
        relocatePopups();
        observePopups();

        /* Vizibilitatea urmărește #mainApp fără polling (§36: zero timere noi). */
        try {
            var app = byId('mainApp');
            if (app && window.MutationObserver) {
                new MutationObserver(function () { applyVisibility(); })
                    .observe(app, { attributes: true, attributeFilter: ['class'] });
            }
        } catch (e) { warn('observer', e); }

        /* Wrapper-ele: `switchTab` și `render` există deja la acest moment. Dacă
           ordinea de încărcare se schimbă vreodată, reîncercăm de câteva ori. */
        function wrapAll() { return wrapSwitchTab() && wrapGoHome() && wrapRender() && wrapAnchorPopup() && wrapRoleGating(); }
        if (!wrapAll()) {
            var tries = 0;
            var iv = setInterval(function () {
                if (wrapAll() || ++tries > 20) clearInterval(iv);
            }, 150);
        }
        /* La redimensionare, popup-urile deschise se re-încadrează. */
        window.addEventListener('resize', function () {
            POPUPS.forEach(function (id) {
                try { placeHeaderPopup(byId(id)); } catch (e) { warn('resizePlace', e); }
            });
        }, false);

        refresh();
    }

    /* ======================================================================
       10. API PUBLIC + AUTOTEST
       ====================================================================== */

    /* Expus pentru testare automată a încadrării, fără a trece prin anchorPopup. */
    window.anchorPopupClampOnly = function (panel) { try { clampPopup(panel); } catch (e) { warn('clampOnly', e); } };

    var TO_NAV = {
        VERSION: 'nav-stage6',
        __loaded: true,
        __stage1a: true,
        MENU: MENU,
        refresh: refresh,
        renderHub: renderHub,
        renderBar: renderBar,
        openFab: openFab,
        closeFab: closeFab,
        openSearch: openSearch,
        closeSearch: closeSearch,
        search: searchAll,

        /* Ce rânduri vede utilizatorul curent, pe hub. Folosit la testare. */
        visibleRows: function (hubId) {
            var def = MENU.hubs[hubId], out = [];
            if (!def) return out;
            def.groups.slice()
                .sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
                .forEach(function (g) {
                    g.rows.filter(rowVisible).forEach(function (r) { out.push(g.id + '/' + r.id); });
                });
            return out;
        },

        /* Raport pentru pentest (F4.d). Arată, pentru rolul CURENT, ce e vizibil
           în meniu ȘI ce permite garda reală — ca să se vadă că sunt decizii
           independente: ascunderea în meniu NU este securitate. */
        audit: function () {
            var role = '';
            try { role = window.currentClubMyRole ? (window.currentClubMyRole() || '(fără rol)') : '?'; } catch (e) { role = '?'; }
            var caps = ['member.manage', 'settings.edit', 'moderation.view', 'session.create',
                'event.manage', 'announcements.post', 'catalog.manage', 'plan.manage'];
            var capMap = {};
            caps.forEach(function (c) { capMap[c] = CAN(c); });
            var tabs = ['dashboard', 'session', 'players', 'catalog', 'admin', 'chat', 'history'];
            var gate = {};
            tabs.forEach(function (t2) {
                try { gate[t2] = (typeof window.canAccessTab === 'function') ? !!window.canAccessTab(t2) : null; }
                catch (e) { gate[t2] = 'eroare'; }
            });
            return {
                rol: role,
                ownerPlatforma: IS_OWNER(),
                clubReadOnly: (typeof window.isClubReadOnly === 'function') ? !!window.isClubReadOnly() : null,
                capabilitati: capMap,
                meniuVizibil: {
                    club: TO_NAV.visibleRows('hubclub'),
                    maiMulte: TO_NAV.visibleRows('hubmore'),
                    profil: TO_NAV.visibleRows('hubprofile')
                },
                gardaRealaSwitchTab: gate
            };
        },

        selfTest: function () {
            function has(n) { try { return typeof window[n] === 'function'; } catch (e) { return false; } }
            var r = {
                version: TO_NAV.VERSION,
                switchTab: has('switchTab'),
                canAccessTab: has('canAccessTab'),
                can: has('can'),
                escHtml: has('escHtml'),
                legacyTabsNode: !!document.querySelector('.tabs [data-tab="dashboard"]'),
                legacyTabsCount: document.querySelectorAll('.tabs [data-tab]').length,
                hubTabsInDom: ['hubclub', 'hubmore', 'hubprofile'].filter(function (h) {
                    return !!byId('tab-' + h) && !!document.querySelector('.tabs [data-tab="' + h + '"]');
                }).length,
                navNode: !!byId('toNav'),
                fabNode: !!byId('toFab'),
                oldFabRemoved: !byId('quickSessionFab'),
                oldMenuRemoved: !byId('tabMenuOverlay') && !byId('tabMenuFabs'),
                switchTabWrapped: !!window.__toNavSwitchWrapped,
                anchorWrapped: !!window.__toNavAnchorWrapped,
                gatingWrapped: !!window.__toNavGatingWrapped,
                clubRoleWrapped: !!window.__toNavClubRoleWrapped,
                flagRolInHeader: NAV_SHOW_ROLE_IN_HEADER,
                sidebarPliat: sideCollapsed(),
                rolCurent: (function () {
                    try { return window.currentClubMyRole ? (window.currentClubMyRole() || '(fara rol)') : '?'; }
                    catch (e) { return 'eroare'; }
                })(),
                tabPermis: (function () {
                    try { return typeof window.canAccessTab === 'function' ? !!window.canAccessTab(CURTAB()) : null; }
                    catch (e) { return 'eroare'; }
                })(),
                renderWrapped: !!window.__toNavRenderWrapped,
                stateSeen: !!ST(),
                userSeen: !!(ST() && ST().currentUser),
                currentTab: CURTAB(),
                navVisible: !!(byId('toNav') && !byId('toNav').classList.contains('hidden')),
                sideToggle: !!byId('toSideToggle'),
                feedbackFabRemoved: !byId('feedbackFab'),
                headerRetired: ['onlineBtn', 'currentUserDisplay', 'settingsBtn']
                    .filter(function (id) { return !!byId(id); }).length,
                rowsClub: TO_NAV.visibleRows('hubclub').length,
                rowsMore: TO_NAV.visibleRows('hubmore').length,
                rowsProfile: TO_NAV.visibleRows('hubprofile').length
            };
            r.ok = r.switchTab && r.canAccessTab && r.can && r.escHtml &&
                r.legacyTabsNode && r.hubTabsInDom === 3 && r.navNode && r.fabNode &&
                r.oldFabRemoved && r.switchTabWrapped && r.renderWrapped;
            return r;
        }
    };

    window.TO_NAV = TO_NAV;
    /* F3 (3 oct. 2026): expus pentru showWelcomeScreen (app-core.js). La deconectare, subsolul legal
       rămânea în meniul lateral / hub-ul Profil (ascunse odată cu aplicația), deci ecranul de bun
       venit apărea fără linkurile legale până la un refresh. EN: exposed so logout restores the footer. */
    window.placeLegalFooter = placeLegalFooter;
    /* Re-randare la comutarea limbii — apelat de array-ul `_live` din i18n.js (§17.2). */
    window._navRetranslate = refresh;

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
